import type React from 'react';
import { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect, useDeferredValue, memo } from 'react';
import {
  Bot,
  Send,
  ChevronDown,
  ChevronUp,
  Paperclip,
  X,
  FileText,
  Image,
  Terminal,
  ClipboardList,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Copy,
  Check,
  Lock,
  Eye,
  EyeOff,
  Square,
  ShieldCheck,
  Skull,
  Puzzle,
  Monitor,
  CalendarClock,
  Globe,
  Brain,
  Lightbulb,
  SquarePen,
} from 'lucide-react';
import { toast } from 'sonner';
import { useVoiceInput } from '../helpers/useVoiceInput';

/** Inline browser SVG icon */
function BrowserIcon({ size = 12 }: { size?: number }) {
  return (
    <svg viewBox="0 0 200 200" width={size} height={size} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <rect x="20" y="40" width="160" height="120" rx="8" fill="none" stroke="currentColor" strokeWidth="10"/>
      <line x1="20" y1="70" x2="180" y2="70" stroke="currentColor" strokeWidth="10"/>
      <circle cx="40" cy="55" r="4" fill="#ef4444"/>
      <circle cx="55" cy="55" r="4" fill="#f59e0b"/>
      <circle cx="70" cy="55" r="4" fill="#10b981"/>
      <rect x="90" y="52" width="80" height="6" rx="3" fill="currentColor" opacity="0.3"/>
    </svg>
  );
}
import { parsePlanBlock, parseExecPlan } from '../helpers/planBlockParser';
import { PlanCard } from './PlanCard';
import { ExecPlanCard } from './ExecPlanCard';
import { useQueryClient } from '../lib/queryClient';
import { postTaskingBotChat } from '../helpers/chatApiClient';
import {
  fetchChatHistory,
  saveChatHistory,
  clearChatHistory,
  fetchConversations,
  createConversation,
} from '../endpoints/taskingbot/chat-history.schema';
import { useAuth } from '../helpers/useAuth';
import { useTranslation, useLocalization } from '../helpers/useLocalization';
import { useAiModels } from '../helpers/useAiModels';
import { useTaskingBotParticipants } from '../helpers/useTaskingBotParticipants';
import { UserAvatar } from './UserAvatar';
import { TaskingBotAvatarPortal } from './TaskingBotAvatarPortal';
import { ConversationSidebar } from './ConversationSidebar';
import { PersonaSelector } from './PersonaSelector';
import { loadInstalledSkills, SkillSearchModal } from './SkillSearchModal';
import { SkillTreeModal } from './SkillTreeModal';
import { TEMPLATE_SKILL_MAP } from '../helpers/templateSkillMap';
import { RichMessageContent } from '../helpers/RichMessageContent';
import { DesktopViewerToggle } from './DesktopViewer';
import { useMissingCredentialPrompt } from '../helpers/useMissingCredentialPrompt';
import { MissingCredentialModal } from './MissingCredentialModal';
import { CommandListModal } from './CommandListModal';
import { ChatAttachmentsLibrary } from './ChatAttachmentsLibrary';
import { SLASH_COMMANDS as GLOBAL_SLASH_COMMANDS } from '../helpers/slashCommands';
import { CREDENTIAL_REQUIREMENTS } from '../helpers/credentialRequirements';
import { postSecret } from '../endpoints/taskingbot/secrets_POST.schema';
import { MessageFeedback } from './MessageFeedback';
import superjson from 'superjson';
import styles from './TaskingBotChat.module.css';

// ── Skill Results Card ───────────────────────────────────────────────────────
function SkillResultsCard({
  skills,
  onInstall,
}: {
  skills: any[];
  onInstall: (skill: any) => void;
}) {
  return (
    <div className={styles.skillResultsGrid}>
      {skills.map((s, i) => (
        <div key={i} className={styles.skillResultCard}>
          <div className={styles.skillResultInfo}>
            <div className={styles.skillResultName}>{s.name || 'Unnamed'}</div>
            <div className={styles.skillResultMeta}>
              {s.author || 'unknown'}
              {s.category ? ` · ${s.category}` : ''}
            </div>
          </div>
          <button onClick={() => onInstall(s)} className={styles.skillResultAddBtn}>
            Add
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Secret Labels Form (inline, not modal) ──────────────────────────────────
function SecretLabelsForm({
  labels,
  integrationLabel,
  onAllSaved,
  onCancel,
}: {
  labels: { label: string; description: string }[];
  integrationLabel?: string;
  onAllSaved: () => void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const [savedLabels, setSavedLabels] = useState<Set<string>>(new Set());
  const [savingLabel, setSavingLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSaveOne = async (label: string) => {
    const val = values[label]?.trim();
    if (!val) return;
    setSavingLabel(label);
    setError(null);
    try {
      await postSecret({ label, value: val });
      toast.success(`${label} saved securely`);
      const next = new Set(savedLabels).add(label);
      setSavedLabels(next);
      // Auto-retry when ALL labels have been saved
      if (next.size >= labels.length) {
        onAllSaved();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSavingLabel(null);
    }
  };

  return (
    <div
      style={{
        margin: '12px 0',
        background: 'var(--card, #1a1a2e)',
        border: '1px solid var(--border, rgba(255,255,255,0.08))',
        borderRadius: 'var(--radius, 12px)',
        overflow: 'hidden',
      }}
    >
      {/* Integration header */}
      {integrationLabel && (
        <div
          style={{
            padding: '12px 20px',
            borderBottom: '1px solid var(--border, rgba(255,255,255,0.06))',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.85rem',
            fontWeight: 600,
          }}
        >
          <Lock size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
          {integrationLabel} — {labels.length} credential{labels.length !== 1 ? 's' : ''} needed
        </div>
      )}
      {labels.map(({ label, description }) => {
        const isSaved = savedLabels.has(label);
        const displayLabel = label
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
          .replace(/ /g, ' ');

        return (
          <div
            key={label}
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border, rgba(255,255,255,0.06))',
              opacity: isSaved ? 0.5 : 1,
              transition: 'opacity 0.3s',
            }}
          >
            {/* Title row */}
            <div
              style={{
                fontSize: '0.9rem',
                fontWeight: 600,
                marginBottom: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Lock size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
              {isSaved ? `${displayLabel} — Saved` : `Enable ${displayLabel}`}
            </div>

            {/* Description */}
            <p
              style={{
                fontSize: '0.78rem',
                opacity: 0.55,
                margin: '0 0 12px',
                color: 'color-mix(in srgb, var(--primary) 70%, currentColor)',
              }}
            >
              {description}.{' '}
              <span style={{ textDecoration: 'underline', cursor: 'pointer', opacity: 0.7 }}>
                Read more
              </span>
            </p>

            {!isSaved && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* Input with eye toggle */}
                <div
                  style={{
                    flex: 1,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <input
                    type={visible[label] ? 'text' : 'password'}
                    placeholder={description || `Enter ${label}`}
                    value={values[label] || ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [label]: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveOne(label)}
                    autoComplete="off"
                    style={{
                      width: '100%',
                      padding: '10px 40px 10px 14px',
                      fontSize: '0.85rem',
                      color: 'inherit',
                      background:
                        'color-mix(in srgb, var(--secondary, hsl(220 10% 25%)) 20%, transparent)',
                      border: '1px solid var(--border, rgba(255,255,255,0.12))',
                      borderRadius: 'var(--radius-sm, 8px)',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => ({ ...prev, [label]: !prev[label] }))}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      opacity: 0.4,
                      cursor: 'pointer',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title={visible[label] ? 'Hide' : 'Show'}
                  >
                    {visible[label] ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>

                {/* Submit button */}
                <button
                  onClick={() => handleSaveOne(label)}
                  disabled={!values[label]?.trim() || savingLabel === label}
                  style={{
                    padding: '10px 20px',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    color: values[label]?.trim() ? '#fff' : 'inherit',
                    background: values[label]?.trim() ? 'var(--primary, #4f6ef7)' : 'transparent',
                    border: '1px solid',
                    borderColor: values[label]?.trim()
                      ? 'var(--primary, #4f6ef7)'
                      : 'var(--border, rgba(255,255,255,0.08))',
                    borderRadius: 'var(--radius-sm, 8px)',
                    cursor: values[label]?.trim() ? 'pointer' : 'not-allowed',
                    opacity: !values[label]?.trim() || savingLabel === label ? 0.4 : 1,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                  }}
                >
                  {savingLabel === label ? 'Saving...' : 'Submit'}
                </button>
              </div>
            )}

            {/* Integration tag */}
            <div
              style={{
                marginTop: '10px',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <span
                style={{
                  fontSize: '0.7rem',
                  opacity: 0.3,
                  fontStyle: 'italic',
                }}
              >
                {integrationName.charAt(0).toUpperCase() + integrationName.slice(1).toLowerCase()}
              </span>
            </div>
          </div>
        );
      })}

      {/* Footer */}
      <div
        style={{
          padding: '10px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: '0.65rem',
            opacity: 0.3,
          }}
        >
          <Lock size={10} /> Encrypted with AES-256 — per-user, never shared
        </div>
        <button
          onClick={onCancel}
          style={{
            padding: '4px 12px',
            fontSize: '0.7rem',
            fontWeight: 500,
            background: 'none',
            border: '1px solid var(--border, rgba(255,255,255,0.08))',
            borderRadius: 'var(--radius-sm, 8px)',
            color: 'inherit',
            opacity: 0.5,
            cursor: 'pointer',
          }}
        >
          Dismiss
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: '10px 20px',
            background: 'color-mix(in srgb, var(--error, #ef4444) 8%, transparent)',
            borderTop: '1px solid color-mix(in srgb, var(--error, #ef4444) 20%, transparent)',
            fontSize: '0.78rem',
            color: 'var(--error, #ef4444)',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

type SlashCommandItem = { cmd: string; args: string; desc: string };

// ── Slash command definitions ─────────────────────────────────
const TASKINGBOT_SLASH_COMMANDS: SlashCommandItem[] = [
  { cmd: '/plan', args: '<goal>', desc: 'Create an interactive plan with guided questions' },
  { cmd: '/skills', args: '[query]', desc: 'Search and install skills from ClawdHub + OpenClaw' },
  { cmd: '/skilltree', args: '', desc: 'Open the skill tree and packs' },
  { cmd: '/create-skill', args: '<name or idea>', desc: 'Create a new custom skill with AI-generated SKILL.md' },
  { cmd: '/create-skills', args: '<name or idea>', desc: 'Same as /create-skill' },
  { cmd: '/create-pack', args: '', desc: 'Create a new skill pack' },
  { cmd: '/image', args: '<prompt>', desc: 'Generate an AI image in chat' },
  { cmd: '/video', args: '<prompt>', desc: 'Generate an AI video clip in chat' },
  { cmd: '/sound', args: '<prompt>', desc: 'Generate AI sound/music in chat' },
  { cmd: '/song', args: '<description>', desc: 'Create a full-length song (2+ minutes) with genre, mood, and lyrics' },
  { cmd: '/create-song', args: '<description>', desc: 'Create a full-length song with vocals, lyrics, and instruments' },
  { cmd: '/voice', args: '<text>', desc: 'Generate AI voice audio in chat' },
  { cmd: '/clear', args: '', desc: 'Clear chat history' },
  { cmd: '/help', args: '', desc: 'Show available commands and skills' },
  { cmd: '/http', args: '<url> <json>', desc: 'POST JSON to any URL (webhooks, APIs)' },
  { cmd: '/get', args: '<url>', desc: 'Fetch content from a URL' },
  { cmd: '/task', args: '<description>', desc: 'Create a task immediately' },
  { cmd: '/memory', args: '<fact>', desc: 'Save a fact to long-term memory' },
  { cmd: '/sow', args: '<project description>', desc: 'Generate a professional Statement of Work PDF' },
  { cmd: '/proposal', args: '<project description>', desc: 'Generate a business proposal PDF' },
  { cmd: '/report', args: '<topic>', desc: 'Generate a professional report PDF' },
  { cmd: '/resume', args: '<name and details>', desc: 'Generate a professional resume/CV PDF' },
  { cmd: '/newsletter', args: '<topic>', desc: 'Generate a professional newsletter PDF' },
  { cmd: '/coverletter', args: '<job + details>', desc: 'Generate a professional cover letter PDF' },
  { cmd: '/flyer', args: '<product or event>', desc: 'Generate a promotional sales flyer PDF' },
  { cmd: '/posts', args: '<topic>', desc: 'Generate social media posts for LinkedIn/Facebook/X with images' },
  { cmd: '/logo', args: '<brand name + style>', desc: 'Generate logo designs with variations' },
  { cmd: '/tshirtlogo', args: '<concept>', desc: 'Generate t-shirt/merch print-ready designs' },
  { cmd: '/poster', args: '<event or topic>', desc: 'Generate a poster design' },
  { cmd: '/sign', args: '<text + purpose>', desc: 'Generate signage designs' },
  { cmd: '/sticker', args: '<concept>', desc: 'Generate sticker designs with die-cut style' },
  { cmd: '/emoji', args: '<concept>', desc: 'Generate custom emoji/icon designs' },
  { cmd: '/techdocs', args: '<product or API>', desc: 'Generate technical documentation PDF' },
  { cmd: '/manual', args: '<product or system>', desc: 'Generate a user manual PDF' },
  { cmd: '/faq', args: '<product or topic>', desc: 'Generate a FAQ document PDF' },
  { cmd: '/book', args: '<title + outline>', desc: 'Generate a book/ebook PDF with chapters' },
  { cmd: '/musicvideo', args: '<concept>', desc: 'Generate a music video concept with storyboard and clips' },
  { cmd: '/tvshow', args: '<concept>', desc: 'Generate a TV show concept with pilot script and visuals' },
  { cmd: '/cartoon', args: '<concept>', desc: 'Generate cartoon/animation concept art and storyboard' },
  { cmd: '/video-anime', args: '<concept>', desc: 'Open the anime video editor' },
  { cmd: '/video-cartoon', args: '<concept>', desc: 'Open the cartoon video editor' },
  { cmd: '/video-cinematic', args: '<concept>', desc: 'Open the cinematic video editor' },
  { cmd: '/video-promo', args: '<concept>', desc: 'Open the promo video editor' },
  { cmd: '/video-commercial', args: '<concept>', desc: 'Open the commercial video editor' },
  { cmd: '/comic', args: '<story>', desc: 'Generate a comic strip or comic book pages' },
  { cmd: '/3d', args: '<prompt>', desc: 'Generate 3D render concept art' },
  { cmd: '/3dasset', args: '<object>', desc: 'Generate 3D asset reference sheets and specs' },
  { cmd: '/3dprint', args: '<object>', desc: 'Generate 3D printable design concept and specs' },
  { cmd: '/game', args: '<concept>', desc: 'Generate game design document with art and mechanics' },
  { cmd: '/website', args: '<description>', desc: 'Generate and deploy a website' },
  { cmd: '/webapp', args: '<description>', desc: 'Generate and deploy a web application' },
  { cmd: '/banner', args: '<text + purpose>', desc: 'Generate web/social banner designs' },
  { cmd: '/decal', args: '<concept>', desc: 'Generate decal/vinyl cut designs' },
  { cmd: '/headshots', args: '<description>', desc: 'Generate professional headshot/portrait photos' },
  { cmd: '/headshot', args: '<description>', desc: 'Generate a professional headshot photo' },
  { cmd: '/portrait', args: '<description>', desc: 'Generate a portrait photo' },
  { cmd: '/profilepic', args: '<description>', desc: 'Generate a profile picture' },
  { cmd: '/powerpoint', args: '<topic>', desc: 'Generate a PowerPoint-style presentation PDF' },
  { cmd: '/diagram', args: '<system or concept>', desc: 'Generate professional diagrams and visual explanations' },
  { cmd: '/workflow', args: '<process>', desc: 'Generate a workflow diagram with steps and decision points' },
  { cmd: '/swimlane', args: '<process + roles>', desc: 'Generate a swimlane diagram showing responsibilities' },
  { cmd: '/gantt', args: '<project plan>', desc: 'Generate a Gantt chart project timeline PDF' },
  { cmd: '/raci', args: '<project + stakeholders>', desc: 'Generate a RACI matrix PDF' },
  { cmd: '/research', args: '<topic>', desc: 'Generate a deep research report PDF with citations' },
  { cmd: '/document', args: '<topic>', desc: 'Generate a general-purpose professional document PDF' },
  { cmd: '/specs', args: '<product or feature>', desc: 'Generate a technical specification document PDF' },
  { cmd: '/analyze', args: '<topic or data>', desc: 'Deep analysis with insights, charts, and recommendations' },
  { cmd: '/debug', args: '<issue description>', desc: 'Systematic debugging analysis with root cause identification' },
  { cmd: '/rca', args: '<incident>', desc: 'Root Cause Analysis report with timeline and corrective actions' },
  { cmd: '/troubleshoot', args: '<problem>', desc: 'Step-by-step troubleshooting guide with decision tree' },
  { cmd: '/proscons', args: '<options>', desc: 'Pros and cons comparison matrix with scoring' },
  { cmd: '/rating', args: '<items to rate>', desc: 'Detailed rating and scoring analysis with criteria' },
  { cmd: '/compare', args: '<items>', desc: 'Side-by-side comparison with charts and recommendations' },
  { cmd: '/secreport', args: '<scope>', desc: 'Generate a security assessment report PDF' },
  { cmd: '/threatanalysis', args: '<system>', desc: 'Generate a threat analysis report with risk matrix' },
  { cmd: '/leads', args: '[status|company|query]', desc: 'Manage your sales pipeline — view, add, and qualify leads' },
  { cmd: '/prospect', args: '<ICP criteria>', desc: 'AI Lead Hunter — find prospects matching your ideal customer profile' },
  { cmd: '/icp', args: '<criteria>', desc: 'Define your Ideal Customer Profile for AI prospecting' },
  { cmd: '/outreach', args: '<target + channel>', desc: 'Create personalized outreach sequences' },
  { cmd: '/enrich', args: '<company or lead>', desc: 'Research and enrich lead profiles with company intel' },
  { cmd: '/score', args: '', desc: 'Score and rank leads against your ICP' },
  { cmd: '/tip', args: '<topic>', desc: 'Generate a professional tips/best-practice guide' },
  { cmd: '/news', args: '<topic>', desc: 'Generate a news brief with analysis and takeaways' },
  { cmd: '/email', args: '<context>', desc: 'Draft a professional email' },
  { cmd: '/log', args: '<timeframe or filter>', desc: 'View activity logs and history' },
  { cmd: '/login', args: '<service>', desc: 'Login or authenticate to a service' },
  { cmd: '/select', args: '<item type>', desc: 'Select or switch between items (tasks, projects, etc.)' },
  { cmd: '/find', args: '<query>', desc: 'Search across tasks, files, messages, and more' },
  { cmd: '/card', args: '<name + details>', desc: 'Generate a professional business card design' },
  { cmd: '/bizcard', args: '<name + details>', desc: 'Generate a business card with multiple style variations' },
  { cmd: '/qrcode', args: '<url or data>', desc: 'Generate a QR code with custom styling' },
  { cmd: '/meme', args: '<concept>', desc: 'Generate a custom meme image with text' },
  { cmd: '/drhouse', args: '<problem>', desc: 'Diagnose like Dr. House — sarcastic, brilliant differential diagnosis' },
  { cmd: '/pentest', args: '<target scope>', desc: 'Generate a penetration test report with findings and remediation' },
  { cmd: '/vulnerabilityreport', args: '<system>', desc: 'Generate a vulnerability assessment report' },
  { cmd: '/threatmatrix', args: '<system>', desc: 'Generate a threat matrix with attack vectors and risk scores' },
  { cmd: '/secaudit', args: '<scope>', desc: 'Generate a security audit report with compliance checks' },
  { cmd: '/compliancereport', args: '<framework>', desc: 'Generate a compliance report (SOC2, HIPAA, CMMC, FIPS, etc.)' },
  { cmd: '/cmmc', args: '<scope>', desc: 'CMMC compliance assessment' },
  { cmd: '/fips', args: '<scope>', desc: 'FIPS compliance assessment' },
  { cmd: '/pci', args: '<scope>', desc: 'PCI-DSS compliance assessment' },
  { cmd: '/soc2', args: '<scope>', desc: 'SOC2 compliance assessment' },
  { cmd: '/hipaa', args: '<scope>', desc: 'HIPAA compliance assessment' },
  { cmd: '/gdpr', args: '<scope>', desc: 'GDPR compliance assessment' },
  { cmd: '/nist', args: '<scope>', desc: 'NIST framework compliance assessment' },
  { cmd: '/pii', args: '<scope>', desc: 'PII data assessment and compliance' },
  { cmd: '/phi', args: '<scope>', desc: 'PHI data assessment and HIPAA compliance' },
  { cmd: '/mxtest', args: '<domain>', desc: 'Test mail records — MX, SPF, DKIM, DMARC, blacklist check' },
  { cmd: '/dnslookup', args: '<domain>', desc: 'DNS lookup — A, AAAA, CNAME, MX, NS, TXT, SOA records' },
  { cmd: '/traceroute', args: '<host>', desc: 'Network path analysis — hosting, CDN, latency, topology' },
  { cmd: '/chat', args: '', desc: 'Switch to conversational mode — talk without generating documents' },
  { cmd: '/tasks', args: '', desc: 'Show current tasks and their status' },
  { cmd: '/sendsms', args: '<phone> <message>', desc: 'Send a text message to a phone number' },
  { cmd: '/convertdoc', args: '[attach PDF]', desc: 'Convert an uploaded PDF to editable DOCX — preserves images, colors, layout' },
];

function usageToArgs(command: string, usage: string): string {
  if (!usage) return '';
  const normalized = usage.trim();
  if (!normalized.startsWith(command)) return normalized;
  return normalized.slice(command.length).trim();
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  attachment?: { name: string; type: string; content: string; size?: number };
  attachments?: Array<{ name: string; type: string; content: string; size?: number }>;
}

interface PendingAttachment {
  name: string;
  type: string;
  content: string; // base64 data URL
  size: number;
}

export type MainPanelTab = 'chat' | 'assets';

// ─── Plan / Brainstorm quick action menu ───────────────────
function PlanMenu({ onSelect }: { onSelect: (cmd: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const items = [
    { icon: '📋', label: 'Create a plan', cmd: '/plan ' },
    { icon: '🧠', label: 'Brainstorm', cmd: '/brainstorm ' },
    { icon: '📊', label: 'Analyze', cmd: '/analyze ' },
    { icon: '🔍', label: 'Research', cmd: '/research ' },
    { icon: '📄', label: 'Generate document', cmd: '/document ' },
    { icon: '📈', label: 'Create report', cmd: '/report ' },
    { icon: '🛡️', label: 'Security audit', cmd: '/secaudit ' },
    { icon: '⏰', label: 'Schedule task', cmd: '/task ' },
    { icon: '🆚', label: 'Compare options', cmd: '/compare ' },
    { icon: '🐛', label: 'Debug issue', cmd: '/debug ' },
  ];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className={styles.attachButton}
        onClick={() => setOpen(!open)}
        title="Plan, brainstorm, analyze..."
        style={open ? { color: 'var(--primary, #f97316)' } : undefined}
      >
        <Lightbulb size={12} />
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: 0,
          marginBottom: 8,
          background: 'var(--surface, #1a2332)',
          border: '1px solid var(--border, #334155)',
          borderRadius: 10,
          boxShadow: '0 8px 24px rgb(0 0 0 / 0.4)',
          padding: '6px 0',
          minWidth: 200,
          zIndex: 100,
          maxHeight: 320,
          overflowY: 'auto',
        }}>
          {items.map((item) => (
            <button
              key={item.cmd}
              onClick={() => { onSelect(item.cmd); setOpen(false); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                padding: '8px 14px',
                background: 'none',
                border: 'none',
                color: 'var(--foreground, #f8fafc)',
                fontSize: '0.8rem',
                cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'color-mix(in srgb, var(--primary, #f97316) 12%, transparent)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <span style={{ fontSize: '1rem' }}>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export type FocusTaskContext = {
  id: string;
  title: string;
  parentTaskId?: string | null;
  nonce: number;
};

/** Map internal tool names to user-friendly labels */
const FRIENDLY_TOOL_NAMES: Record<string, string> = {
  list_secrets: 'checking integrations',
  propose_secrets: 'requesting credentials',
  update_task: 'updating task',
  create_task: 'creating task',
  search_tasks: 'searching tasks',
  web_search: 'searching the web',
  web_fetch: 'fetching page',
  web_scrape: 'scraping site',
  scrape_url: 'scraping site',
  http_get: 'fetching page',
  http_post: 'sending request',
  browse_page: 'browsing page',
  generate_image: 'generating image',
  generate_video: 'generating video',
  generate_audio: 'generating audio',
  generate_pdf: 'generating PDF',
  remove_background: 'removing background',
  enhance_image: 'enhancing image',
  store_learning: 'learning',
  get_learnings: 'recalling',
  save_credentials: 'saving credentials',
  list_credentials: 'checking credentials',
  send_email: 'sending email',
  browse: 'browsing',
  click: 'clicking',
  screenshot: 'taking screenshot',
};
function friendlyToolName(name: string): string {
  return FRIENDLY_TOOL_NAMES[name] || name.replace(/_/g, ' ');
}

function ordSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

const STORAGE_KEY = 'taskingbot-messages';
const TASK_CONVERSATION_MAP_KEY = 'taskingbot-task-conversations-v1';

function loadLocalMessages(): ChatMessage[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const raw: ChatMessage[] = JSON.parse(stored);
    const msgs = raw.filter((m) => m && m.role);
    // One-time cleanup: remove stale attachments duplicated on every message by a previous bug.
    // If more than half the messages share the exact same attachment, strip them all.
    const withAttach = msgs.filter((m) => m.attachment);
    if (withAttach.length > 1) {
      const first = JSON.stringify(withAttach[0].attachment);
      const dupeCount = withAttach.filter((m) => JSON.stringify(m.attachment) === first).length;
      if (dupeCount > msgs.length * 0.4) {
        try { localStorage.removeItem('taskingbot-last-attachment'); } catch {}
        return msgs.map((m) => (m.attachment ? { ...m, attachment: undefined } : m));
      }
    }
    return msgs;
  } catch {
    return [];
  }
}

function saveLocalMessages(messages: ChatMessage[]) {
  try {
    // Strip large base64 content from attachments to avoid exceeding localStorage quota (~5MB).
    // Keep name/type so we can show file badges on reload; images won't re-render after refresh.
    const slim = messages.map((m) => {
      if (!m.attachment && !m.attachments) return m;
      const stripContent = (a: any) => ({ name: a.name, type: a.type, content: '', size: a.size });
      return {
        ...m,
        attachment: m.attachment ? stripContent(m.attachment) : undefined,
        attachments: m.attachments?.map(stripContent),
      };
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
  } catch {
    // localStorage may be full — try without attachments at all
    try {
      const bare = messages.map((m) => ({ id: m.id, role: m.role, content: m.content, createdAt: m.createdAt }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(bare));
    } catch { /* truly full, give up */ }
  }
}

function getTaskConversationLookupKey(projectId: string | null, taskId: string): string {
  return `${projectId || 'global'}::${taskId}`;
}

function loadTaskConversationMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(TASK_CONVERSATION_MAP_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveTaskConversationMap(value: Record<string, string>): void {
  try {
    localStorage.setItem(TASK_CONVERSATION_MAP_KEY, JSON.stringify(value));
  } catch {
    // Ignore storage failures.
  }
}

/**
 * Try to recover the latest assistant response from the server DB.
 * On mobile, the browser kills fetch streams when the app goes to background,
 * but the server finishes processing and saves the response. This function
 * re-fetches conversation history and returns the last assistant message if
 * it was created after our request started.
 */
async function tryRecoverFromServer(
  convId: string | null | undefined,
  requestStartedAt: number,
): Promise<string | null> {
  // The server may still be processing (tool calls, image gen, PDF rendering, etc.).
  // PDF generation can take 2-5 minutes, so retry with longer delays.
  const delays = [3000, 5000, 10000, 15000, 20000, 30000, 30000, 30000];
  for (const delay of delays) {
    try {
      await new Promise((r) => setTimeout(r, delay));
      const data = await fetchChatHistory({
        limit: 5,
        ...(convId ? { conversationId: convId } : {}),
      });
      if (data.messages && data.messages.length > 0) {
        const lastMsg = data.messages[data.messages.length - 1];
        if (
          lastMsg.role === 'assistant' &&
          lastMsg.content &&
          lastMsg.content.trim().length > 0 &&
          new Date(lastMsg.createdAt).getTime() >= requestStartedAt - 5000
        ) {
          return lastMsg.content;
        }
      }
    } catch (e) {
      console.warn('[TaskingBot] Recovery fetch failed:', e);
    }
  }
  return null;
}

/** Save new messages to DB (fire-and-forget, errors logged silently). */
function saveMessagesToDb(
  msgs: Array<{ role: 'user' | 'assistant'; content: string; attachment?: any }>,
  convId?: string | null,
) {
  if (msgs.length === 0) return;
  saveChatHistory(msgs, convId ?? undefined).catch((err) => {
    console.warn('[TaskingBot] DB save failed, localStorage still has messages:', err);
  });
}

const MODEL_STORAGE_KEY = 'taskingbot-selected-model';
const MULTI_AGENT_STORAGE_KEY = 'taskingbot-multi-agent-mode';
const AGENT_MODE_STORAGE_KEY = 'taskingbot-agent-mode';
const INPUT_HISTORY_KEY = 'taskingbot-input-history';
const MAX_INPUT_HISTORY = 200;

function getInputHistoryKey(taskId?: string | null): string {
  return taskId ? `${INPUT_HISTORY_KEY}-${taskId}` : INPUT_HISTORY_KEY;
}

function loadInputHistory(taskId?: string | null): string[] {
  try {
    const stored = localStorage.getItem(getInputHistoryKey(taskId));
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveInputHistory(history: string[], taskId?: string | null) {
  try {
    localStorage.setItem(getInputHistoryKey(taskId), JSON.stringify(history));
  } catch {
    // Ignore localStorage persistence errors.
  }
}

// ── Global stream persistence — survives React unmount/remount ──────────
// When the component unmounts (tab switch, orientation change, mobile panel swap),
// the active stream reader continues processing in the background. On remount,
// the component picks up the accumulated content and resumes live display.
interface ActiveStreamState {
  reader: ReadableStreamDefaultReader<Uint8Array> | null;
  abortController: AbortController | null;
  accumulated: string;
  messages: ChatMessage[];
  conversationId: string | null;
  isActive: boolean;
  onChunk?: (content: string) => void;
}
const _globalStreams = new Map<string, ActiveStreamState>();

function getStreamKey(conversationId?: string | null, taskId?: string | null): string {
  return taskId ? `task:${taskId}` : conversationId || 'default';
}

export function TaskingBotChat({
  projectId,
  panelTab,
  onPanelTabChange,
  taskId,
}: {
  projectId: string | null;
  panelTab?: MainPanelTab;
  onPanelTabChange?: (tab: MainPanelTab) => void;
  taskId?: string | null;
} = {}) {
  const isTaskMode = !!taskId;
  const { authState } = useAuth();
  const { t } = useTranslation();
  const { language } = useLocalization();
  const { data: modelsData } = useAiModels();
  const queryClient = useQueryClient();
  const { activeParticipants } = useTaskingBotParticipants();
  const credentialPrompt = useMissingCredentialPrompt();
  const [messages, setMessages] = useState<ChatMessage[]>(() => isTaskMode ? [] : loadLocalMessages());
  // Defer message rendering so typing stays responsive even with many messages
  const deferredMessages = useDeferredValue(messages);
  const [dbLoaded, setDbLoaded] = useState(false);
  const [pendingMessageIds, setPendingMessageIds] = useState<Set<string>>(new Set());
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [input, setInput] = useState('');
  const [slashSuggestions, setSlashSuggestions] = useState<SlashCommandItem[]>([]);
  const [slashSelectedIdx, setSlashSelectedIdx] = useState(0);
  // Ctrl+R history recall
  const [recallMode, setRecallMode] = useState(false);
  const [recallQuery, setRecallQuery] = useState('');
  const [recallResults, setRecallResults] = useState<string[]>([]);
  const [recallSelectedIdx, setRecallSelectedIdx] = useState(0);

  // Dynamic suggestion chips from user's recent activity
  const [dynamicSuggestions, setDynamicSuggestions] = useState<{ label: string; prompt: string; icon?: string }[]>([]);
  useEffect(() => {
    if (isTaskMode) return; // No suggestions in task mode
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/_api/taskingbot/suggestions', { credentials: 'include' });
        if (!res.ok || cancelled) return;
        const text = await res.text();
        let data: any;
        try { data = (await import('superjson')).default.parse(text); } catch { data = JSON.parse(text); }
        if (!cancelled && data.suggestions) setDynamicSuggestions(data.suggestions);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [isTaskMode]);
  const [isCmdListOpen, setIsCmdListOpen] = useState(false);
  const [isSkillSearchOpen, setIsSkillSearchOpen] = useState(false);
  const [skillSearchQuery, setSkillSearchQuery] = useState('');
  const [isSkillTreeOpen, setIsSkillTreeOpen] = useState(false);
  const [skillTreeMode, setSkillTreeMode] = useState<'view' | 'create'>('view');
  // pendingSecretLabels removed — form now renders inline inside message bubbles
  const [subscriptionError, setSubscriptionError] = useState<{
    requiredTier?: string;
    message?: string;
  } | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>(() => {
    const PREFERRED_DEFAULT = 'glm-5.1';
    const PREFERRED_MODELS = ['glm-5.1', 'glm-5', 'qwen3-coder-next', 'deepseek-v3.2'];
    try {
      let stored = localStorage.getItem(MODEL_STORAGE_KEY) || '';
      // Fix legacy model names that no longer exist
      if (stored.endsWith(':cloud')) stored = stored.replace(':cloud', '');
      // Reset to default if stored model isn't in the preferred list
      if (stored && !PREFERRED_MODELS.some(m => stored.startsWith(m))) {
        localStorage.removeItem(MODEL_STORAGE_KEY);
        return PREFERRED_DEFAULT;
      }
      return stored || PREFERRED_DEFAULT;
    } catch {
      return PREFERRED_DEFAULT;
    }
  });
  const [multiAgentMode, setMultiAgentMode] = useState<'off' | 'parallel' | 'sequential'>(() => {
    try {
      const stored = localStorage.getItem(MULTI_AGENT_STORAGE_KEY);
      return (stored as 'off' | 'parallel' | 'sequential') || 'off';
    } catch {
      return 'off';
    }
  });
  type AgentMode = 'auto' | 'cloud_browser' | 'extension' | 'desktop';
  const [agentMode, setAgentMode] = useState<AgentMode>(() => {
    try {
      const stored = localStorage.getItem(AGENT_MODE_STORAGE_KEY);
      return (stored as AgentMode) || 'auto';
    } catch {
      return 'auto';
    }
  });
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [voiceOutputEnabled, setVoiceOutputEnabled] = useState(false);
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    frequency: '' as '' | 'once' | 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'weekdays' | 'custom',
    customSchedule: '',
    dueDate: '',
    dueTime: '',
    selectedDays: [] as string[],
  });
  const schedulePopupRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showSchedulePopup) return;
    const handler = (e: MouseEvent) => {
      if (schedulePopupRef.current && !schedulePopupRef.current.contains(e.target as Node)) {
        setShowSchedulePopup(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSchedulePopup]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  // Sidebar UI state — persists open/closed across reloads.
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const v = window.localStorage.getItem('taskingbot-sidebar-open');
    return v === null ? true : v === '1';
  });
  // Bumped to force the sidebar to refetch its conversation list (after a new
  // message has been sent or a chat has been deleted).
  const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);
  // When the user clicks a conversation in the sidebar, this overrides the
  // auto-pick effect below. Cleared when the user starts a new chat.
  const [userPickedConvId, setUserPickedConvId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem('taskingbot-active-conv-id') || null;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('taskingbot-sidebar-open', sidebarOpen ? '1' : '0');
  }, [sidebarOpen]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (userPickedConvId) window.localStorage.setItem('taskingbot-active-conv-id', userPickedConvId);
    else window.localStorage.removeItem('taskingbot-active-conv-id');
  }, [userPickedConvId]);

  // Bump the sidebar refresh key whenever a message exchange completes so
  // auto-derived titles + last-message previews stay current.
  const lastMsgCountRef = useRef(0);
  useEffect(() => {
    if (messages.length > lastMsgCountRef.current) {
      lastMsgCountRef.current = messages.length;
      const t = setTimeout(() => setSidebarRefreshKey((k) => k + 1), 1500);
      return () => clearTimeout(t);
    }
  }, [messages.length]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [browserViewer, setBrowserViewer] = useState<{
    url: string;
    title: string;
    screenshot: string;
    success: boolean;
    actions: number;
    status?: string;
    isBlobUrl?: boolean;
    sessionId?: string;
  } | null>(null);
  const [browserViewerExpanded, setBrowserViewerExpanded] = useState(false);
  const [browserUserControl, setBrowserUserControl] = useState(false);
  const [browserInputText, setBrowserInputText] = useState('');
  const [browserZoom, setBrowserZoom] = useState<1 | 2 | 4>(1);
  const browserScrollThrottle = useRef<number>(0);
  const [activeToolProgress, setActiveToolProgress] = useState<{ name: string; status: 'scanning' | 'passed' | 'running' | 'done' | 'error' | 'blocked' }[]>([]);
  const [activeDesktopActions, setActiveDesktopActions] = useState<{ type: string; detail: string; status: 'pending' | 'running' | 'done' }[]>([]);
  const browserFramePollRef = useRef<number | null>(null);
  const browserWsRef = useRef<WebSocket | null>(null);
  // VNC-like action sender: WebSocket first, HTTP fallback
  const sendBrowserAction = useCallback((sessionId: string, payload: Record<string, any>) => {
    const ws = browserWsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'action', ...payload }));
      return;
    }
    // HTTP fallback
    fetch('/_api/taskingbot/browser-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ sessionId, ...payload }),
    }).catch((e) => console.error('[BrowserAction fallback]', e));
  }, []);
  const [internalMainPanelTab, setInternalMainPanelTab] = useState<MainPanelTab>('chat');
  const mainPanelTab = panelTab ?? internalMainPanelTab;
  const setMainPanelTab = useCallback(
    (tab: MainPanelTab) => {
      if (panelTab === undefined) {
        setInternalMainPanelTab(tab);
      }
      onPanelTabChange?.(tab);
    },
    [onPanelTabChange, panelTab],
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasAnchoredBottomRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastUserMessageRef = useRef<string>(''); // Track last user message for auto-retry after credential save
  const sendMessageRef = useRef<(() => void | Promise<void>) | null>(null);
  const queuedMessagesRef = useRef<string[]>([]); // Messages queued while streaming
  // Input history navigation (like terminal ↑/↓)
  const inputHistoryRef = useRef<string[]>(loadInputHistory(taskId));
  const historyIndexRef = useRef<number>(-1); // -1 = current draft
  const draftInputRef = useRef<string>(''); // saved draft when navigating history

  // ── Reconnect to active global stream on mount (survives unmount/remount) ──
  // If the component was unmounted while a stream was active (tab switch, orientation
  // change, mobile panel swap), reconnect to the running stream and resume display.
  useEffect(() => {
    const sk = getStreamKey(conversationId, taskId);
    const gs = _globalStreams.get(sk);
    if (gs && gs.isActive && gs.accumulated) {
      console.log(`[TaskingBot] Reconnecting to active stream (${gs.accumulated.length} chars accumulated)`);
      // Restore messages from before unmount
      if (gs.messages.length > 0) {
        setMessages(gs.messages);
      }
      // Show accumulated content
      setStreamingContent(gs.accumulated);
      setPendingMessageIds(new Set(['reconnected']));
      // Update the onChunk callback to point to this component instance
      gs.onChunk = (content: string) => {
        setStreamingContent(content);
      };
      // Restore abort controller
      if (gs.abortController) {
        abortRef.current = gs.abortController;
      }
    } else if (gs && !gs.isActive && gs.accumulated) {
      // Stream completed while we were unmounted — show final result
      console.log(`[TaskingBot] Stream completed while unmounted, showing final result`);
      if (gs.messages.length > 0) {
        setMessages(gs.messages);
      }
      _globalStreams.delete(sk);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload input history when taskId changes
  useEffect(() => {
    inputHistoryRef.current = loadInputHistory(taskId);
    historyIndexRef.current = -1;
    draftInputRef.current = '';
  }, [taskId]);

  // Persist model selection
  useEffect(() => {
    try {
      localStorage.setItem(MODEL_STORAGE_KEY, selectedModel);
    } catch {
      // Ignore localStorage persistence errors.
    }
  }, [selectedModel]);

  // Persist multi-agent mode
  useEffect(() => {
    try {
      localStorage.setItem(MULTI_AGENT_STORAGE_KEY, multiAgentMode);
    } catch {
      // Ignore localStorage persistence errors.
    }
  }, [multiAgentMode]);

  // Persist agent mode
  useEffect(() => {
    try {
      localStorage.setItem(AGENT_MODE_STORAGE_KEY, agentMode);
    } catch {}
  }, [agentMode]);

  // Available models: cloud (Ollama Cloud) + local when enabled
  const availableModels = useMemo(() => {
    const cloud = modelsData?.cloudModels?.filter((m) => m.provider === 'ollama_cloud') || [];
    // Ensure glm-5 is always an option even if not returned by API
    if (!cloud.find((m) => m.model === 'glm-5.1' || m.model === 'glm-5:cloud')) {
      cloud.unshift({
        provider: 'ollama_cloud',
        model: 'glm-5.1',
        name: 'GLM 5.1',
        tags: ['Default'],
      });
    }
    // Include local models when local LLM is enabled
    const local =
      modelsData?.localEnabled && modelsData.localModels
        ? modelsData.localModels.map((m) => ({
            provider: 'ollama_local' as const,
            model: m.model,
            name: `${m.name}${m.size ? ` (${m.size})` : ''} [Local]`,
            tags: ['Local'] as string[],
          }))
        : [];
    return [...cloud, ...local];
  }, [modelsData]);

  // Load ALL skills to pass to TaskingBot: installed skills + all template skills
  const activeSkillsPayload = useMemo(() => {
    try {
      const installed = loadInstalledSkills();
      const activeInstalled = installed
        .filter((s) => s.active)
        .map((s) => ({ name: s.name, content: s.content, description: s.description }));

      // Merge all template-mapped command skills (framework-specific knowledge)
      const installedNames = new Set(activeInstalled.map((s) => s.name));
      const templateSkills = Object.values(TEMPLATE_SKILL_MAP)
        .filter((ts) => !installedNames.has(ts.name))
        .map((ts) => ({ name: ts.name, description: ts.description }));

      return [...activeInstalled, ...templateSkills];
    } catch {
      return [];
    }
  }, []);

  const allSlashCommands = useMemo<SlashCommandItem[]>(() => {
    const merged = new Map<string, SlashCommandItem>();
    for (const cmd of TASKINGBOT_SLASH_COMMANDS) {
      merged.set(cmd.cmd, cmd);
    }
    for (const cmd of GLOBAL_SLASH_COMMANDS) {
      const normalized: SlashCommandItem = {
        cmd: cmd.command,
        args: usageToArgs(cmd.command, cmd.usage),
        desc: cmd.description,
      };
      if (!merged.has(normalized.cmd)) {
        merged.set(normalized.cmd, normalized);
      }
      for (const alias of cmd.aliases || []) {
        if (!merged.has(alias)) {
          merged.set(alias, {
            cmd: alias,
            args: usageToArgs(alias, cmd.usage),
            desc: `${cmd.description} (alias of ${cmd.command})`,
          });
        }
      }
    }
    return Array.from(merged.values()).sort((a, b) => a.cmd.localeCompare(b.cmd));
  }, []);

  // Resolve (or create) a conversation for the active project, then load its messages.
  // In task mode, load from task-messages endpoint instead.
  useEffect(() => {
    if (authState.type !== 'authenticated') return;
    let cancelled = false;

    (async () => {
      try {
        // Task mode: load messages from task-messages endpoint
        if (isTaskMode && taskId) {
          const res = await fetch(`/_api/taskingbot/task-messages?taskId=${encodeURIComponent(taskId)}`, { credentials: 'include' });
          if (cancelled) return;
          if (res.ok) {
            const text = await res.text();
            let data: any;
            try { data = superjson.parse(text); } catch { try { data = JSON.parse(text); } catch { data = {}; } }
            const msgs = (data.messages || []).map((m: any) => ({
              id: String(m.id),
              role: m.role as 'user' | 'assistant',
              content: m.content,
              createdAt: m.createdAt,
            }));
            setMessages(msgs);
          } else {
            setMessages([]);
          }
          setDbLoaded(true);
          return;
        }

        let convId: string | undefined;

        // Use a conversation ID to prevent cross-pollination between chats.
        // If the user explicitly picked one in the sidebar, honour that. Otherwise
        // fall back to auto-pick (first conversation under the active project).
        if (userPickedConvId) {
          convId = userPickedConvId;
        } else {
          try {
            const effectiveProjectId = projectId || '__global__';
            const { conversations } = await fetchConversations({ projectId: effectiveProjectId });
            if (cancelled) return;
            if (conversations.length > 0) {
              convId = conversations[0].id;
            } else {
              const created = await createConversation({ projectId: effectiveProjectId });
              if (cancelled) return;
              convId = (created as any)?.conversation?.id || (created as any)?.id;
            }
          } catch (convErr) {
            console.warn('[TaskingBot] Conversation setup failed, using unscoped mode:', convErr);
          }
        }
        setConversationId(convId || null);

        // Load messages scoped to this conversation (or all if global)
        const data = await fetchChatHistory({
          limit: 200,
          ...(convId ? { conversationId: convId } : {}),
        });
        if (cancelled) return;

        if (data.messages && data.messages.length > 0) {
          const dbMsgs: ChatMessage[] = data.messages.map((m) => ({
            id: m.id,
            role: m.role as 'user' | 'assistant',
            content: m.content,
            createdAt: m.createdAt,
            attachment: m.attachment,
          }));
          setMessages(dbMsgs);
          saveLocalMessages(dbMsgs);
        } else {
          setMessages([]);
        }
        setDbLoaded(true);
      } catch (err) {
        console.warn('[TaskingBot] Conversation/history load failed:', err);
        if (!cancelled) setDbLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authState, projectId, isTaskMode, taskId, userPickedConvId]);

  // Persist messages to localStorage (always, as offline cache) — skip in task mode
  useEffect(() => {
    if (!isTaskMode) saveLocalMessages(messages);
  }, [messages, isTaskMode]);

  // In task mode, periodically refetch messages so autonomous updates appear
  useEffect(() => {
    if (!isTaskMode || !taskId || authState.type !== 'authenticated') return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/_api/taskingbot/task-messages?taskId=${encodeURIComponent(taskId)}`, { credentials: 'include' });
        if (!res.ok) return;
        const text = await res.text();
        let data: any;
        try { data = superjson.parse(text); } catch { try { data = JSON.parse(text); } catch { return; } }
        const msgs = (data.messages || []).map((m: any) => ({
          id: String(m.id),
          role: m.role as 'user' | 'assistant',
          content: m.content,
          createdAt: m.createdAt,
        }));
        setMessages((prev) => {
          if (msgs.length !== prev.length) return msgs;
          // Only update if content changed
          const lastNew = msgs[msgs.length - 1];
          const lastOld = prev[prev.length - 1];
          if (lastNew?.content !== lastOld?.content) return msgs;
          return prev;
        });
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [isTaskMode, taskId, authState]);

  // When tab becomes visible again, check if server completed a response
  // while we were backgrounded (stream may have died but server kept going).
  // Use refs for latest state to avoid recreating the listener on every render
  // (which caused stale closures and race conditions on mobile tab switches).
  const conversationIdRef = useRef(conversationId);
  const pendingMessageIdsRef = useRef(pendingMessageIds);
  const messagesRef = useRef(messages);
  useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);
  useEffect(() => { pendingMessageIdsRef.current = pendingMessageIds; }, [pendingMessageIds]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  useEffect(() => {
    let recovering = false;
    const onVisible = async () => {
      if (document.hidden || recovering) return;
      if (pendingMessageIdsRef.current.size === 0) return;
      recovering = true;
      try {
        // Small delay — let the browser fully resume networking before fetching
        await new Promise((r) => setTimeout(r, 500));
        const convId = conversationIdRef.current;
        const data = await fetchChatHistory({
          limit: 5,
          ...(convId ? { conversationId: convId } : {}),
        });
        if (data.messages && data.messages.length > 0) {
          const latest = data.messages[data.messages.length - 1];
          if (latest.role === 'assistant') {
            const alreadyHave = messagesRef.current.some(m => m.id === latest.id);
            if (!alreadyHave) {
              const newMsg: ChatMessage = {
                id: latest.id,
                role: 'assistant',
                content: latest.content,
                createdAt: latest.createdAt,
              };
              setMessages(prev => [...prev, newMsg]);
              setPendingMessageIds(new Set());
              setStreamingContent('');
            }
          }
        }
      } catch {
        // Silently ignore — the stream error handler will also attempt recovery
      } finally {
        recovering = false;
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []); // Empty deps — uses refs for latest state

  useEffect(() => {
    setMainPanelTab('chat');
  }, [projectId]);

  // Anchor to the bottom immediately when the chat first opens/loads instead of visibly animating down.
  useLayoutEffect(() => {
    if (!dbLoaded || mainPanelTab !== 'chat') return;
    // Immediate scroll + delayed scroll to catch late-rendering messages
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    }, 300);
    hasAnchoredBottomRef.current = true;
    return () => clearTimeout(timer);
  }, [dbLoaded, mainPanelTab, conversationId, projectId]);

  // Poll for placeholder updates when async PDF is generating.
  // The Fly pipeline writes [TOOL_EXEC:name:status] markers into the
  // placeholder row in D1 as it progresses, then replaces it with the
  // final PDF result. We need to: (a) merge updated placeholder content
  // into local state, (b) extract TOOL_EXEC markers into activeToolProgress.
  useEffect(() => {
    if (authState.type !== 'authenticated') return;
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant') return;
    const looksLikeAsyncPlaceholder =
      /Generating your PDF|please wait/i.test(lastMsg.content) ||
      /^\[TOOL_EXEC:/m.test(lastMsg.content);
    if (!looksLikeAsyncPlaceholder) return;

    // If content already has final result, stop polling
    if (lastMsg.content?.includes('[media:pdf]') || lastMsg.content?.includes('PDF generation failed')) {
      setActiveToolProgress([]);
      return;
    }

    const placeholderId = lastMsg.id;
    let consecutiveErrors = 0;
    const pollInterval = setInterval(async () => {
      try {
        const data = await fetchChatHistory({ limit: 20, ...(conversationId ? { conversationId } : {}) });
        consecutiveErrors = 0; // reset on success
        const allMessages = data.messages || [];
        if (allMessages.length === 0) return;

        // Find the matching placeholder row (by id, OR most-recent assistant
        // row whose content has placeholder markers).
        const matchingRow = allMessages.find(m => String(m.id) === String(placeholderId)) || allMessages
          .filter(m => m.role === 'assistant' && typeof m.content === 'string' && (
            m.content.startsWith('[TOOL_EXEC:') ||
            /Generating your PDF|please wait/i.test(m.content) ||
            m.content.includes('[media:pdf]') ||
            m.content.includes('PDF generation failed') ||
            m.content.includes('PDF generation could not')
          ))
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        if (matchingRow) {
          const newContent = String(matchingRow.content);
          // Extract TOOL_EXEC badges
          const map = new Map<string, 'running' | 'done' | 'error' | 'blocked'>();
          const matches = newContent.matchAll(/\[TOOL_EXEC:([^:]+):(running|done|error|blocked)\]/g);
          for (const m of matches) map.set(m[1], m[2] as any);
          if (map.size > 0) {
            setActiveToolProgress(Array.from(map.entries()).map(([name, status]) => ({ name, status })));
          }
          if (newContent.includes('[media:pdf]')) {
            setActiveToolProgress([]);
          }
          // Merge updated content into local state
          setMessages(prev => {
            const idx = prev.findIndex(m => String(m.id) === String(placeholderId));
            if (idx === -1) {
              // Placeholder ID not found locally — this is the first poll match,
              // adopt it as our placeholder
              const exists = prev.some(m => m.content === newContent);
              if (exists) return prev;
              const lastIdx = prev.length - 1;
              if (lastIdx >= 0 && prev[lastIdx].role === 'assistant' && /Generating your PDF|please wait/i.test(prev[lastIdx].content)) {
                const next = prev.slice();
                next[lastIdx] = { ...prev[lastIdx], id: matchingRow.id, content: newContent };
                return next;
              }
              return prev;
            }
            if (prev[idx].content === newContent) return prev;
            const next = prev.slice();
            next[idx] = { ...prev[idx], content: newContent };
            return next;
          });
        }
      } catch (e: any) {
        consecutiveErrors++;
        // Back off on repeated errors (429 rate limit, network issues)
        if (consecutiveErrors >= 5) {
          clearInterval(pollInterval);
          console.warn('[placeholder-poll] Stopped after 5 consecutive errors');
        }
      }
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [messages, conversationId, authState.type]);

  // Smooth-scroll only after the chat is already anchored.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: hasAnchoredBottomRef.current ? 'smooth' : 'auto',
      block: 'end',
    });
    hasAnchoredBottomRef.current = true;
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 160) + 'px';
    }
  }, [input]);

  // Voice input
  const {
    isListening,
    transcript,
    isSupported: isVoiceSupported,
    startListening,
    stopListening,
    resetTranscript,
  } = useVoiceInput({
    onAutoSend: (message: string) => {
      // Combine with existing input if any
      const base = input;
      const combined = base ? `${base} ${message}` : message;
      setInput(combined);
      resetTranscript();
      // Auto-send after input is updated
      setTimeout(() => {
        // Use the combined message directly
        if (combined.trim() || attachments.length > 0) {
          // Trigger send by simulating the message in input
          setInput(combined);
          setTimeout(() => {
            void sendMessageRef.current?.();
          }, 50);
        }
      }, 0);
    },
  });

  const handleVoiceToggle = () => {
    if (isListening) {
      stopListening();
    } else {
      // Interrupt TTS when user starts speaking
      import('../helpers/aiBotVoice').then(({ botVoice }) => botVoice.stop()).catch(() => {});
      resetTranscript();
      startListening();
    }
  };

  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);

  /**
   * Keep assistant output readable for end users while preserving markdown content.
   * Do NOT strip TASKINGBOT UI markers that power in-bubble rendering
   * (e.g. [SKILL_RESULTS], [PLAN_BLOCK], [EXEC_PLAN], [SECRET_LABELS_NEEDED]).
   */
  // Strip all content between [TAG]...[/TAG] markers using indexOf (fast, no regex backtracking on large base64)
  const stripTagBlocks = (str: string, openTag: string, closeTag: string): string => {
    let result = str;
    let safety = 0;
    while (safety++ < 100) {
      const start = result.indexOf(openTag);
      if (start === -1) break;
      const end = result.indexOf(closeTag, start + openTag.length);
      if (end === -1) {
        // Incomplete tag (still streaming) — strip from the open tag to end of string
        // so raw base64/JSON payloads don't leak into chat text
        result = result.substring(0, start);
        break;
      }
      result = result.substring(0, start) + result.substring(end + closeTag.length);
    }
    return result;
  };

  /** Extract the JSON payload from a [CRED_REQUIRED:{...}] marker, handling nested brackets. */
  const extractCredRequired = (text: string): string | null => {
    const tag = '[CRED_REQUIRED:';
    const startIdx = text.indexOf(tag);
    if (startIdx < 0) return null;
    const jsonStart = startIdx + tag.length;
    let depth = 0;
    for (let i = jsonStart; i < text.length; i++) {
      const ch = text[i];
      if (ch === '{' || ch === '[') depth++;
      if (ch === '}' || ch === ']') depth--;
      if (depth < 0) return text.slice(jsonStart, i);
    }
    return null;
  };

  const sanitizeAssistantDisplayContent = (text: string) => {
    // First strip large binary blocks using fast indexOf
    let cleaned = stripTagBlocks(text, '[BROWSER_FRAME]', '[/BROWSER_FRAME]');
    cleaned = stripTagBlocks(cleaned, '[BROWSER_STATUS]', '[/BROWSER_STATUS]');
    cleaned = stripTagBlocks(cleaned, '[BROWSER_SESSION]', '[/BROWSER_SESSION]');
    cleaned = stripTagBlocks(cleaned, '[ACTIONS]', '[/ACTIONS]');
    cleaned = stripTagBlocks(cleaned, '[ACTION_RESULTS]', '[/ACTION_RESULTS]');
    cleaned = stripTagBlocks(cleaned, '[DESKTOP_ACTIONS]', '[/DESKTOP_ACTIONS]');
    cleaned = stripTagBlocks(cleaned, '[DESKTOP_ACTION_REPORT]', '[/DESKTOP_ACTION_REPORT]');
    cleaned = stripTagBlocks(cleaned, '[STORED_CREDENTIALS]', '[/STORED_CREDENTIALS]');
    cleaned = stripTagBlocks(cleaned, '[CLOUD_BROWSER_REPORT]', '[/CLOUD_BROWSER_REPORT]');
    // Strip ALL internal protocol tags and raw JSON/code that should never show in chat
    cleaned = cleaned.replace(/\[?\/?DESKTOP_ACTIONS\]?/gi, '');
    cleaned = cleaned.replace(/\[?\/?ACTIONS\]?/gi, '');
    cleaned = cleaned.replace(/\[?\/?ACTION_REPORT\]?/gi, '');
    cleaned = cleaned.replace(/\[?\/?DESKTOP_ACTION_REPORT\]?/gi, '');
    cleaned = cleaned.replace(/\[?\/?CLOUD_BROWSER_REPORT\]?/gi, '');
    cleaned = cleaned.replace(/\[?\/?INTERNAL_AUTH_CONTEXT\]?/gi, '');
    cleaned = cleaned.replace(/\[?\/?FORM_FIELDS\]?/gi, '');
    cleaned = cleaned.replace(/\[?\/?VISIBLE_BUTTONS?\]?/gi, '');
    cleaned = cleaned.replace(/\[?\/?DESKTOP_STATUS\]?/gi, '');
    cleaned = cleaned.replace(/\[?\/?EXTENSION_STATUS\]?/gi, '');
    cleaned = cleaned.replace(/\[?\/?DEVICE_CONTEXT\]?/gi, '');
    cleaned = cleaned.replace(/\]?\s*\/?CTIONS\]/gi, '');
    cleaned = cleaned.replace(/\[DE\]\[\/?\s*/gi, '');
    // Strip any JSON object with "actions" key
    cleaned = cleaned.replace(/\{"actions"\s*:\s*\[[\s\S]*?\]\s*\}/gi, '');
    // Strip bare JSON arrays of action objects
    cleaned = cleaned.replace(/\[\s*\{\s*"(?:type|id)"\s*:\s*"[a-z_]+"[\s\S]*?\}\s*\]/gi, '');
    // Strip any multi-line JSON object blocks (3+ lines of {"key": "value"})
    cleaned = cleaned.replace(/\{\s*\n\s*"[a-zA-Z_]+"\s*:\s*[\s\S]*?\n\s*\}/g, '');
    // Strip partial/garbled blocks like [A\n[{"type":...
    cleaned = cleaned.replace(/\[A\s*\n?\s*\[?\{[\s\S]*?\}\]?\s*/gi, '');
    // Strip TypeScript/code fragments that leak (import statements, function declarations, etc.)
    cleaned = cleaned.replace(/^import\s+\{[^}]+\}\s+from\s+['"][^'"]+['"];?\s*$/gm, '');
    cleaned = cleaned.replace(/^(?:export\s+)?(?:async\s+)?function\s+\w+\s*\([^)]*\)\s*\{[\s\S]*?\}\s*$/gm, '');
    cleaned = cleaned.replace(/^const\s+\w+\s*=\s*(?:await\s+)?fetch\s*\([^)]+\)[\s\S]*?;?\s*$/gm, '');
    // Strip console.log/warn/error lines
    cleaned = cleaned.replace(/^console\.\w+\([\s\S]*?\);\s*$/gm, '');

    const normalized = cleaned
      .replace(/\r\n?/g, '\n')
      .replace(/^\[PLACEHOLDER_ID:\d+\]\n?/gm, '')
      .replace(/\n?\[TOKEN_USAGE:\d+\]\n?/gi, '\n\n')
      .replace(/\n?\[EXTENSION_ACTIONS:[^\]]*\]\n?/gi, '\n\n')
      .replace(/\n?\[EXTENSION_RESTRICTED\][^\n]*\n?/gi, '\n\n')
      .replace(/\n?\[CRED_REQUIRED:[^\]]*\]\n?/gi, '\n\n')
      .replace(/\[TOOL_SCAN:[^\]]*\]\n?/gi, '')
      .replace(/\[TOOL_EXEC:[^\]]*\]\n?/gi, '')
      .replace(/```json\s*\{[\s\S]*?"actions"\s*:\s*\[[\s\S]*?\}\s*```/gi, '\n\n');

    const filteredLines = normalized
      .split('\n')
      .filter((line) => {
        const trimmed = line.trim();
        if (!trimmed) return true;
        if (
          /^(?:monitor\.js:\d+|window\.fetch\s+@|Fetch finished loading:|virtual:ascii-art:|quillworks\.[^\s]+)/i.test(
            trimmed,
          )
        ) {
          return false;
        }
        if (/^[=+*#@]{20,}$/.test(trimmed)) return false;
        // Strip browser viewer status lines — these show inside the viewer, not in chat
        if (/^🌐\s+Browsing the web/i.test(trimmed)) return false;
        if (/^🌐\s+Running \d+ (?:cloud )?browser action/i.test(trimmed)) return false;
        if (/^📸\s+Navigated to/i.test(trimmed)) return false;
        if (/^⚠️\s+Some (?:cloud )?browser actions failed/i.test(trimmed)) return false;
        if (/^✅\s+Cloud browser actions completed/i.test(trimmed)) return false;
        if (/^⚠️\s+Tool ".*" called \d+ times/i.test(trimmed)) return false;
        if (/^⚠️\s+Subtask limit reached/i.test(trimmed)) return false;
        // Strip internal task/learning bookkeeping
        if (/✅\s*Task created:/i.test(trimmed)) return false;
        if (/✅\s*Task \w[\w-]+\s+updated/i.test(trimmed)) return false;
        if (/✅\s*Learning stored:/i.test(trimmed)) return false;
        if (/✅\s*Stored (?:learning|insight)/i.test(trimmed)) return false;
        // Strip garbled action tag fragments
        if (/^\]?\s*\/?[AC]?CTIONS\]?\s*$/i.test(trimmed)) return false;
        if (/^\]\s*\]$/.test(trimmed)) return false;
        if (/^\]$/.test(trimmed)) return false;
        // Strip bare URLs leaked by browser agent (model narrating navigate targets)
        if (/^https?:\/\/\S+\s*$/.test(trimmed)) return false;
        // Strip "Switching to alternative approach" and similar tech spam
        if (/^Switching to alternative approach/i.test(trimmed)) return false;
        if (/^Navigating to\s/i.test(trimmed)) return false;
        if (/^I'll navigate to\s/i.test(trimmed)) return false;
        if (/^Let me (?:navigate|go|browse|open|try|click|type|fill)/i.test(trimmed)) return false;
        if (/^I'll (?:now |try |click |type |fill |open |go )/i.test(trimmed)) return false;
        if (/^Now (?:navigating|clicking|typing|filling|opening|going)/i.test(trimmed)) return false;
        // Strip "Generated X/Y images" progress
        if (/^Generated \d+\/\d+ images? for:/i.test(trimmed)) return false;
        return true;
      })
      .join('\n')
      .replace(/\n{4,}/g, '\n\n\n')
      .trimEnd();

    return filteredLines;
  };

  /** Strip internal markers from message text */
  const cleanMessageContent = (text: string) =>
    sanitizeAssistantDisplayContent(text)
      // Remove internal block wrappers.
      .replace(/\n?\[SKILL_RESULTS\][\s\S]*?\[\/SKILL_RESULTS\]\n?/g, '')
      .replace(/\n?\[ASSET_IMAGE\][\s\S]*?\[\/ASSET_IMAGE\]\n?/g, '')
      .replace(/\n?\[SECRET_LABELS_NEEDED:[^\]]*\]\n?/g, '')
      .replace(/\n?\[PLAN_BLOCK\]?[\s\S]*?\[\/PLAN_BLOCK\]\n?/g, '')
      .replace(/\n?\[EXEC_PLAN\][\s\S]*?\[\/EXEC_PLAN\]\n?/g, '')
      .replace(/\[EXEC_STEP:\d+:(?:active|done|failed|skipped)\]\n?/g, '')
      .replace(/\[EXEC_STEP:complete\]\n?/g, '')
      .replace(/\n?\[TOOL_STATUS:[^\]]*\]\n?/g, '')
      .replace(/\[TOOL_START:[^\]]*\]\n?/g, '')
      .replace(/\[\/TOOL_END\]\n?/g, '')
      .replace(/\n?\[media:(?:image|video|audio|pdf|3d)\]\s*/gi, '\n')
      // Keep links readable in docs when pasted.
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1: $2')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1: $2')
      // Remove markdown wrappers so pasted text reads naturally in docs.
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*\n]+)\*/g, '$1')
      .replace(/`([^`\n]+)`/g, '$1')
      // Remove stray HTML-like internal tags.
      .replace(/<\/?think>/g, '')
      // Normalize spacing for clean paste into docs.
      .replace(/\n{3,}/g, '\n\n')
      .trim();

  /** Copy full assistant response — gathers all consecutive assistant messages in the group */
  const handleCopyMessage = useCallback((msgId: string, _content: string) => {
    const msgIndex = messages.findIndex((m) => m.id === msgId);
    if (msgIndex === -1) return;

    // Find the start of the consecutive assistant message block
    let start = msgIndex;
    while (start > 0 && messages[start - 1].role === 'assistant') start--;
    // Find the end
    let end = msgIndex;
    while (end < messages.length - 1 && messages[end + 1].role === 'assistant') end++;

    // Combine all consecutive assistant messages
    const combinedParts: string[] = [];
    for (let i = start; i <= end; i++) {
      if (messages[i].role === 'assistant') {
        const cleaned = cleanMessageContent(messages[i].content);
        if (cleaned) combinedParts.push(cleaned);
      }
    }
    const fullResponse = combinedParts.join('\n\n');

    navigator.clipboard
      .writeText(fullResponse)
      .then(() => {
        setCopiedMsgId(msgId);
        toast.success('Copied clean response');
        setTimeout(() => setCopiedMsgId(null), 2000);
      })
      .catch(() => toast.error('Failed to copy'));
  }, [messages]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const newAttach: PendingAttachment = {
          name: file.name,
          type: file.type,
          content: ev.target?.result as string,
          size: file.size,
        };
        setAttachments((prev) => [...prev, newAttach]);
      };
      reader.readAsDataURL(file);
    });
    // Reset input so the same file can be re-selected
    e.target.value = '';
  }, []);

  // Drag & drop handlers - allow users to drop image files or image URLs into the chat
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (_e: React.DragEvent) => {
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const dt = e.dataTransfer;

    // If files were dropped, prefer them
    if (dt && dt.files && dt.files.length > 0) {
      Array.from(dt.files).forEach((file) => {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const result = ev.target?.result as string;
          if (result) {
            setAttachments((prev) => [...prev, {
              name: file.name,
              type: file.type,
              content: result,
              size: file.size,
            }]);
            const fileName = (file.name || 'file').split(/[/\\]/).pop() || file.name;
            toast.success(result.startsWith('data:image/') ? 'Image attached' : `File attached: ${fileName}`);
          } else {
            toast.error('Could not read that file. Try a different format.');
          }
        };
        reader.onerror = () => {
          toast.error('Could not read that file. Try a different format.');
        };
        reader.readAsDataURL(file);
      });
      return;
    }

    // If no file, check for a text/URL (dragging image from another tab or desktop may give a URL)
    const url = dt?.getData('text/uri-list') || dt?.getData('text/plain');
    if (url && url.startsWith('http')) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`);
        const blob = await response.blob();
        if (!blob.type.startsWith('image/')) {
          toast.error("That link doesn't seem to be an image");
          return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
          const result = ev.target?.result as string;
          if (result && result.startsWith('data:image/')) {
            setAttachments((prev) => [...prev, {
              name: url.split('/').pop()?.split('?')[0] || 'image.png',
              type: blob.type,
              content: result,
              size: blob.size,
            }]);
            toast.success('Image attached from URL');
          }
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error('[TaskingBotChat] Failed to fetch dropped URL image:', err);
        toast.error('Could not load that image. Try saving it first.');
      }
    }
  };

  // Handle paste for clipboard images
  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    const text = e.clipboardData?.getData('text/plain');

    // PRIORITY 1: Check for actual image data in clipboard items FIRST
    // This handles normal copy-paste of images from browsers, screenshots, etc.
    if (items) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => {
              const result = ev.target?.result as string;
              // Validate that we got a proper data URL, not a file path
              if (result && result.startsWith('data:image/')) {
                setAttachments((prev) => [...prev, {
                  name: file.name || 'pasted-image.png',
                  type: file.type,
                  content: result,
                  size: file.size,
                }]);
                toast.success('Image attached');
              } else {
                console.error(
                  '[TaskingBotChat] Invalid image data - expected data URL, got:',
                  result?.substring(0, 100),
                );
              }
            };
            reader.onerror = (err) => {
              console.error('[TaskingBotChat] Failed to read pasted image:', err);
            };
            reader.readAsDataURL(file);
          }
          return; // Found image data, exit early
        }
      }
    }

    // PRIORITY 2: Pasted image URL (https://...)
    const trimmed = text?.trim() || '';
    if (trimmed.match(/^https?:\/\/.+/i)) {
      // Check if it looks like an image URL or might resolve to one
      const looksLikeImage = trimmed.match(/\.(png|jpe?g|gif|webp|svg|bmp|ico|avif)(\?.*)?$/i);

      if (looksLikeImage) {
        e.preventDefault();
        const cleanName = trimmed.split('/').pop()?.split('?')[0] || 'pasted-image.png';
        console.log('[TaskingBotChat] Image URL pasted:', trimmed);
        try {
          const resp = await fetch(trimmed);
          if (resp.ok) {
            const contentType = resp.headers.get('content-type') || '';
            if (contentType.startsWith('image/')) {
              const blob = await resp.blob();
              const dataUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
              if (dataUrl.startsWith('data:image/')) {
                setAttachments((prev) => [...prev, {
                  name: cleanName,
                  type: blob.type,
                  content: dataUrl,
                  size: blob.size,
                }]);
                toast.success('Image attached from URL');
                return;
              }
            }
          }
        } catch (err) {
          console.log('[TaskingBotChat] Failed to fetch image URL:', err);
        }
        // Fetch failed — let it paste as text so the user can still see/use the URL
        // Re-insert the text since we prevented default
        document.execCommand('insertText', false, trimmed);
        return;
      }
    }

    // PRIORITY 3: HTML with <img> tag (e.g. right-click "Copy image" in some browsers)
    const html = e.clipboardData?.getData('text/html');
    if (html) {
      const imgMatch = html.match(/<img[^>]+src=["']([^"']+)["']/i);
      if (imgMatch?.[1] && imgMatch[1].startsWith('http')) {
        const imgUrl = imgMatch[1];
        e.preventDefault();
        const cleanName = imgUrl.split('/').pop()?.split('?')[0] || 'pasted-image.png';
        console.log('[TaskingBotChat] Image URL from pasted HTML:', imgUrl);
        try {
          const resp = await fetch(imgUrl);
          if (resp.ok) {
            const blob = await resp.blob();
            if (blob.type.startsWith('image/')) {
              const dataUrl = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
              if (dataUrl.startsWith('data:image/')) {
                setAttachments((prev) => [...prev, {
                  name: cleanName,
                  type: blob.type,
                  content: dataUrl,
                  size: blob.size,
                }]);
                toast.success('Image attached from URL');
                return;
              }
            }
          }
        } catch (err) {
          console.log('[TaskingBotChat] Failed to fetch image from HTML:', err);
        }
      }
    }

    // PRIORITY 4: Local file path (Electron/Pinokio)
    if (
      text &&
      (text.includes('.pinokio-temp') || text.match(/\.(png|jpg|jpeg|gif|webp)$/i)) &&
      !trimmed.startsWith('http')
    ) {
      e.preventDefault();
      const filePath = trimmed;
      const cleanName = filePath.split(/[/\\]/).pop() || 'pasted-image.png';
      console.log('[TaskingBotChat] Detected file path in clipboard:', filePath);

      let loaded = false;

      // Try to fetch via local URL (Pinokio serves temp files)
      if (!loaded) {
        try {
          const fetchUrl = filePath.startsWith('/') ? filePath : `/${filePath}`;
          const resp = await fetch(fetchUrl);
          if (resp.ok && resp.headers.get('content-type')?.startsWith('image/')) {
            const blob = await resp.blob();
            const dataUrl = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            if (dataUrl.startsWith('data:image/')) {
              setAttachment({
                name: cleanName,
                type: blob.type,
                content: dataUrl,
                size: blob.size,
              });
              toast.success('Image attached');
              console.log('[TaskingBotChat] Image loaded via fetch:', cleanName);
              loaded = true;
            }
          }
        } catch (fetchErr) {
          console.log('[TaskingBotChat] Fetch approach failed:', fetchErr);
        }
      }

      // Try Clipboard API read() for actual file data
      if (!loaded) {
        try {
          const clipboardItems = await navigator.clipboard.read();
          for (const item of clipboardItems) {
            for (const type of item.types) {
              if (type.startsWith('image/')) {
                const blob = await item.getType(type);
                const dataUrl = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result as string);
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });
                if (dataUrl.startsWith('data:image/')) {
                  setAttachment({
                    name: cleanName,
                    type: blob.type,
                    content: dataUrl,
                    size: blob.size,
                  });
                  toast.success('Image attached');
                  console.log('[TaskingBotChat] Image loaded via Clipboard API:', cleanName);
                  loaded = true;
                  break;
                }
              }
            }
            if (loaded) break;
          }
        } catch (clipboardErr) {
          console.log('[TaskingBotChat] Clipboard API read failed:', clipboardErr);
        }
      }

      // Try Pinokio/Electron IPC if available
      if (!loaded && (window as any).electronAPI?.readFile) {
        try {
          const fileData = await (window as any).electronAPI.readFile(filePath);
          if (fileData) {
            setAttachment({
              name: cleanName,
              type: 'image/png',
              content: fileData,
              size: 0,
            });
            toast.success('Image attached');
            loaded = true;
          }
        } catch (electronErr) {
          console.log('[TaskingBotChat] Electron IPC failed:', electronErr);
        }
      }

      // Try pterm command if in Pinokio environment
      if (!loaded && (window as any).pterm?.clipboard?.read) {
        try {
          const imgData = await (window as any).pterm.clipboard.read();
          if (imgData && imgData.startsWith('data:image/')) {
            setAttachment({
              name: cleanName,
              type: 'image/png',
              content: imgData,
              size: 0,
            });
            toast.success('Image attached');
            loaded = true;
          }
        } catch (ptermErr) {
          console.log('[TaskingBotChat] Pterm clipboard failed:', ptermErr);
        }
      }

      if (!loaded) {
        toast.error('Could not load image from clipboard. Use the attach button instead.', {
          duration: 4000,
        });
      }
    }
  };

  const sendMessageText = useCallback(
    async (text: string, { skipUserBubble }: { skipUserBubble?: boolean } = {}) => {
      if (!text.trim()) return;
      const userMessageId = Date.now().toString();
      let full = '';
      let updatedMessages: ChatMessage[];
      if (skipUserBubble) {
        // Message bubble was already added (e.g. queued message) — use current messages
        updatedMessages = [...messages];
      } else {
        const userMessage: ChatMessage = {
          id: userMessageId,
          role: 'user',
          content: text.trim(),
          createdAt: new Date().toISOString(),
        };
        updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
      }
      setPendingMessageIds((prev) => new Set([...prev, userMessageId]));
      const abortController = new AbortController();
      try {
        const history = updatedMessages.map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        }));
        history.pop();
        const response = await postTaskingBotChat(
          {
            message: text.trim(),
            history: history.length > 0 ? history : undefined,
            model: selectedModel || undefined,
            conversationId: conversationId || undefined,
            language: language !== 'en' ? language : undefined,
          },
          { signal: abortController.signal },
        );
        if (!response.body) throw new Error('No response received');
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let lastSanitizeAt2 = 0;
        let sanitizeRafId2: number | null = null;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value, { stream: true });
          full += chunk;
          // Extract browser viewer frames — indexOf for speed on large base64 payloads
          {
            const FRAME_OPEN = '[BROWSER_FRAME]';
            const FRAME_CLOSE = '[/BROWSER_FRAME]';
            const lastFrameStart = full.lastIndexOf(FRAME_OPEN);
            if (lastFrameStart !== -1) {
              const jsonStart = lastFrameStart + FRAME_OPEN.length;
              const closeIdx = full.indexOf(FRAME_CLOSE, jsonStart);
              if (closeIdx !== -1) {
                try {
                  setBrowserViewer(JSON.parse(full.substring(jsonStart, closeIdx)));
                } catch (e) {
                  console.error('[BrowserViewer] Parse error (autonomy):', e);
                }
              }
            }
          }
          // Extract browser status updates
          {
            const STATUS_OPEN = '[BROWSER_STATUS]';
            const STATUS_CLOSE = '[/BROWSER_STATUS]';
            const lastStatusStart = full.lastIndexOf(STATUS_OPEN);
            if (lastStatusStart !== -1) {
              const textStart = lastStatusStart + STATUS_OPEN.length;
              const closeIdx = full.indexOf(STATUS_CLOSE, textStart);
              if (closeIdx !== -1) {
                const statusText = full.substring(textStart, closeIdx).trim();
                setBrowserViewer((prev) => prev ? { ...prev, status: statusText } : {
                  url: '', title: '', screenshot: '', success: true, actions: 0, status: statusText,
                });
              }
            }
          }
          // Show streaming content — throttled to avoid expensive sanitize on every chunk
          const now2 = performance.now();
          if (now2 - lastSanitizeAt2 > 150) {
            lastSanitizeAt2 = now2;
            if (sanitizeRafId2) cancelAnimationFrame(sanitizeRafId2);
            sanitizeRafId2 = requestAnimationFrame(() => {
              setStreamingContent(sanitizeAssistantDisplayContent(full));
              sanitizeRafId2 = null;
            });
          }
        }
        if (sanitizeRafId2) cancelAnimationFrame(sanitizeRafId2);
        setStreamingContent('');
        // Stop polling and clear browser viewer after stream ends
        if (browserFramePollRef.current) {
          clearInterval(browserFramePollRef.current);
          browserFramePollRef.current = null;
        }
        setBrowserViewer((prev) => {
          if (prev?.screenshot) setTimeout(() => setBrowserViewer(null), 30000);
          return prev;
        });
        const formattedAssistant = sanitizeAssistantDisplayContent(full);
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now().toString(),
            role: 'assistant',
            content: formattedAssistant,
            createdAt: new Date().toISOString(),
          },
        ]);
        saveMessagesToDb(
          [
            { role: 'user', content: text.trim() },
            { role: 'assistant', content: formattedAssistant },
          ],
          conversationId,
        );
        // ── Async PDF polling: if the bot offloaded to Fly.io, poll for the result ──
        if (formattedAssistant.includes('working on your document') || formattedAssistant.includes('appear in this chat when ready')) {
          const pollConvId = conversationId;
          let pollAttempts = 0;
          const maxPollAttempts = 36; // 36 * 10s = 6 minutes
          const pollInterval = setInterval(async () => {
            pollAttempts++;
            if (pollAttempts > maxPollAttempts) {
              clearInterval(pollInterval);
              return;
            }
            try {
              const pollData = await fetchChatHistory({
                limit: 5,
                ...(pollConvId ? { conversationId: pollConvId } : {}),
              });
              if (pollData?.messages) {
                // Live placeholder updates: find the in-progress assistant row
                // and merge its current content into our local state so live
                // tool badges + final result show up.
                const placeholderMsg = pollData.messages.find(
                  (m: any) =>
                    m.role === 'assistant' &&
                    typeof m.content === 'string' &&
                    (m.content.startsWith('Generating your PDF') ||
                      m.content.startsWith('[TOOL_EXEC:') ||
                      m.content.includes('[media:pdf]') ||
                      m.content.includes('PDF generation failed') ||
                      m.content.includes('PDF generation could not')),
                );
                if (placeholderMsg) {
                  // Update tool progress badges from the polled content
                  const content = String(placeholderMsg.content);
                  const map = new Map<string, 'running' | 'done' | 'error' | 'blocked'>();
                  const matches = content.matchAll(/\[TOOL_EXEC:([^:]+):(running|done|error|blocked)\]/g);
                  for (const m of matches) map.set(m[1], m[2] as any);
                  if (map.size > 0) {
                    setActiveToolProgress(Array.from(map.entries()).map(([name, status]) => ({ name, status })));
                  }

                  // Merge updated placeholder content into local state
                  setMessages((prev) => {
                    const idx = prev.findIndex(
                      (m) =>
                        m.role === 'assistant' &&
                        typeof m.content === 'string' &&
                        (m.content.startsWith('Generating your PDF') || m.content.startsWith('[TOOL_EXEC:')),
                    );
                    if (idx === -1) return prev;
                    if (prev[idx].content === content) return prev;
                    const next = prev.slice();
                    next[idx] = { ...prev[idx], content };
                    return next;
                  });
                }

                const pdfMsg = pollData.messages.find(
                  (m: any) => m.role === 'assistant' && m.content?.includes('[media:pdf]'),
                );
                if (pdfMsg) {
                  clearInterval(pollInterval);
                  setActiveToolProgress([]);
                  // Add the PDF message if not already displayed
                  setMessages((prev) => {
                    const exists = prev.some((m) => m.content?.includes('[media:pdf]') && m.content === pdfMsg.content);
                    if (exists) return prev;
                    return [
                      ...prev,
                      {
                        id: pdfMsg.id || Date.now().toString(),
                        role: 'assistant' as const,
                        content: pdfMsg.content,
                        createdAt: pdfMsg.createdAt || new Date().toISOString(),
                      },
                    ];
                  });
                }
                // Also check for error messages from async pipeline
                const errMsg = pollData.messages.find(
                  (m: any) => m.role === 'assistant' && m.content?.includes('PDF generation') && m.content?.includes('failed'),
                );
                if (errMsg) {
                  clearInterval(pollInterval);
                  setMessages((prev) => {
                    const exists = prev.some((m) => m.content === errMsg.content);
                    if (exists) return prev;
                    return [
                      ...prev,
                      {
                        id: errMsg.id || Date.now().toString(),
                        role: 'assistant' as const,
                        content: errMsg.content,
                        createdAt: errMsg.createdAt || new Date().toISOString(),
                      },
                    ];
                  });
                }
              }
            } catch (pollErr: any) {
              // On 429 rate limit, don't burn poll attempts
              if (pollErr?.message?.includes('429') || pollErr?.message?.includes('Too Many')) {
                pollAttempts--; // undo the increment for this cycle
              }
            }
          }, 10_000);
        }
        // Speak the response if voice is enabled
        try {
          const { botVoice } = await import('../helpers/aiBotVoice');
          if (botVoice.enabled) {
            botVoice.speakReply(formattedAssistant);
          }
        } catch {}
        // Refresh tasks — tool calls may have created/updated tasks
        queryClient.invalidateQueries({ queryKey: ['taskingbot', 'tasks'] });
        queryClient.invalidateQueries({ queryKey: ['taskingbot', 'projects'] });
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          // User-initiated cancel — ignore
        } else {
          // Stream interrupted (mobile tab switch). Server continues processing.
          // Always try recovery first to get the COMPLETE response.
          const recovered = await tryRecoverFromServer(conversationId, parseInt(userMessageId, 10));
          if (recovered) {
            const formattedRecovered = sanitizeAssistantDisplayContent(recovered);
            setMessages((prev) => [
              ...prev,
              {
                id: Date.now().toString(),
                role: 'assistant',
                content: formattedRecovered,
                createdAt: new Date().toISOString(),
              },
            ]);
          } else if (full && full.trim().length > 0) {
            // Recovery failed but we have partial content — show without error
            const formattedPartial = sanitizeAssistantDisplayContent(full);
            setMessages((prev) => [
              ...prev,
              {
                id: Date.now().toString(),
                role: 'assistant',
                content: formattedPartial,
                createdAt: new Date().toISOString(),
              },
            ]);
            saveMessagesToDb(
              [
                { role: 'user', content: text.trim() },
                { role: 'assistant', content: formattedPartial },
              ],
              conversationId,
            );
          } else {
            setMessages((prev) => [
              ...prev,
              {
                id: Date.now().toString(),
                role: 'assistant',
                content: 'The connection was lost while generating your response. Please try again — if this was a document, try a shorter prompt or just the command (e.g. /sow).',
                createdAt: new Date().toISOString(),
              },
            ]);
          }
        }
      } finally {
        setPendingMessageIds((prev) => {
          const next = new Set(prev);
          next.delete(userMessageId);
          return next;
        });
      }
    },
    [messages, selectedModel, conversationId],
  );

  const handleEnhanceImage = useCallback(
    async (imageUrl: string) => {
      if (!imageUrl) return;
      const statusId = Date.now().toString();
      setMessages((prev) => [
        ...prev,
        { id: statusId, role: 'assistant', content: 'Enhancing image...', createdAt: new Date().toISOString() },
      ]);
      try {
        const endpoints = ['/_api/taskingbot/enhance-image', '/api/taskingbot/enhance-image'];
        let data: any = null;
        let lastError: Error | null = null;

        for (const endpoint of endpoints) {
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ image_url: imageUrl, scale: 2 }),
          });
          const nextData = await res.json().catch(() => ({}));
          if (res.ok && nextData?.url) {
            data = nextData;
            break;
          }
          lastError = new Error(nextData?.error || `Enhancement failed (${res.status})`);
          if (res.status !== 404) {
            throw lastError;
          }
        }

        if (!data?.url) {
          throw lastError || new Error('Enhancement endpoint was not found');
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === statusId
              ? { ...m, content: `Image enhanced (2x upscale)\n\n[media:image] ${data.url}` }
              : m,
          ),
        );
      } catch (err: any) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === statusId
              ? { ...m, content: `Enhancement failed: ${err?.message || 'Unknown error'}` }
              : m,
          ),
        );
      }
    },
    [],
  );

  const handleRemoveBackground = useCallback(
    async (imageUrl: string) => {
      if (!imageUrl) return;
      console.log('[TaskingBot] Remove background clicked for:', imageUrl);

      // Find the message that contains this image and replace in-place
      const escUrl = imageUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const imgPattern = new RegExp(
        `(\\[media:image\\]\\s*)${escUrl}|(!\\[[^\\]]*\\]\\()${escUrl}(\\))|(\\bsrc=["'])${escUrl}(["'])`,
      );

      // Mark the image as loading in the original message
      setMessages((prev) =>
        prev.map((m) => {
          if (!m.content.includes(imageUrl)) return m;
          return { ...m, _removingBgUrl: imageUrl } as any;
        }),
      );

      try {
        const endpoints = ['/_api/taskingbot/remove-background', '/api/taskingbot/remove-background'];
        let data: any = null;
        let lastError: Error | null = null;

        for (const endpoint of endpoints) {
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ image_url: imageUrl }),
          });
          const nextData = await res.json().catch(() => ({}));
          if (res.ok && nextData?.url) {
            data = nextData;
            break;
          }
          const errMsg = typeof nextData?.error === 'string' ? nextData.error : nextData?.error ? JSON.stringify(nextData.error) : `Background removal failed (${res.status})`;
          lastError = new Error(errMsg);
          if (res.status !== 404) {
            throw lastError;
          }
        }

        if (!data?.url) {
          throw lastError || new Error('Remove background endpoint was not found');
        }

        // Replace the original image URL with the bg-removed URL in the same message
        setMessages((prev) =>
          prev.map((m) => {
            if (!m.content.includes(imageUrl)) return m;
            const updated = m.content.split(imageUrl).join(data.url);
            const cleaned = { ...m, content: updated };
            delete (cleaned as any)._removingBgUrl;
            return cleaned;
          }),
        );
      } catch (err: any) {
        console.error('[TaskingBot] Remove background failed:', err.message);
        toast.error(err.message || 'Background removal failed');
        // Clear loading state on failure
        setMessages((prev) =>
          prev.map((m) => {
            if (!(m as any)._removingBgUrl) return m;
            const cleaned = { ...m };
            delete (cleaned as any)._removingBgUrl;
            return cleaned;
          }),
        );
      }
    },
    [],
  );

  const sendMessage = useCallback(async () => {
    // Interrupt any active TTS when user sends a message
    import('../helpers/aiBotVoice').then(({ botVoice }) => botVoice.stop()).catch(() => {});
    // Clear any stale subscription error so the user can retry
    if (subscriptionError) setSubscriptionError(null);
    const trimmed = input.trim();
    if (!trimmed && attachments.length === 0) return;

    // ── Handle slash commands ──
    setSlashSuggestions([]);

    if (trimmed === '/clearattachment' || trimmed === '/clearlogo') {
      setAttachments([]);
      try { localStorage.removeItem('taskingbot-last-attachment'); } catch {}
      toast.info('Attachments cleared');
      setInput('');
      return;
    }

    if (trimmed === '/clear') {
      setAttachments([]);
      try { localStorage.removeItem('taskingbot-last-attachment'); } catch {}
      setMessages([]);
      localStorage.removeItem(STORAGE_KEY);
      clearChatHistory(conversationId ?? undefined).catch(() => {});
      setInput('');
      return;
    }

    if (trimmed === '/help') {
      const helpText = allSlashCommands.map((c) => `**${c.cmd}** ${c.args} — ${c.desc}`).join('\n');
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          role: 'assistant',
          content: `**Available slash commands:**\n\n${helpText}`,
          createdAt: new Date().toISOString(),
        },
      ]);
      setInput('');
      return;
    }

    if (trimmed === '/cmdlist') {
      setIsCmdListOpen(true);
      setInput('');
      return;
    }

    // If currently streaming, queue the message to send after stream finishes
    const isCurrentlyStreaming = pendingMessageIds.size > 0 || !!streamingContent;
    if (isCurrentlyStreaming) {
      queuedMessagesRef.current.push(trimmed);
      // Show queued message as a user bubble immediately so user gets feedback
      const queuedUserMsg: ChatMessage = {
        id: `queued-${Date.now()}`,
        role: 'user',
        content: trimmed,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, queuedUserMsg]);
      setInput('');
      return;
    }

    let normalizedMessage = trimmed;
    if (trimmed.startsWith('/http ')) {
      const rest = trimmed.slice(6).trim();
      const spaceIdx = rest.indexOf(' ');
      const url = spaceIdx > -1 ? rest.slice(0, spaceIdx) : rest;
      const body = spaceIdx > -1 ? rest.slice(spaceIdx + 1) : '{}';
      normalizedMessage = `http_post to ${url} with body ${body}`;
    } else if (trimmed.startsWith('/get ')) {
      normalizedMessage = `fetch the content from ${trimmed.slice(5).trim()}`;
    } else if (trimmed.startsWith('/task ')) {
      normalizedMessage = `create a task: ${trimmed.slice(6).trim()}`;
    } else if (trimmed.startsWith('/memory ')) {
      normalizedMessage = `save this to my memory: ${trimmed.slice(8).trim()}`;
    } else if (trimmed.startsWith('/image ')) {
      normalizedMessage = `Generate an image with this prompt: ${trimmed.slice(7).trim()}`;
    } else if (trimmed.startsWith('/video-anime ')) {
      normalizedMessage = `Generate a short AI anime-style video for this prompt: ${trimmed.slice(13).trim()}`;
    } else if (trimmed.startsWith('/video-cartoon ')) {
      normalizedMessage = `Generate a short AI cartoon-style video for this prompt: ${trimmed.slice(15).trim()}`;
    } else if (trimmed.startsWith('/video-cinematic ')) {
      normalizedMessage = `Generate a short AI cinematic film-quality video for this prompt: ${trimmed.slice(17).trim()}`;
    } else if (trimmed.startsWith('/video-promo ')) {
      normalizedMessage = `Generate a short AI promotional video for this prompt: ${trimmed.slice(12).trim()}`;
    } else if (trimmed.startsWith('/video-commercial ')) {
      normalizedMessage = `Generate a short AI commercial/advertisement video for this prompt: ${trimmed.slice(18).trim()}`;
    } else if (trimmed.startsWith('/video ')) {
      normalizedMessage = `Generate a short AI video for this prompt: ${trimmed.slice(7).trim()}`;
    } else if (trimmed.startsWith('/song ') || trimmed.startsWith('/create-song ')) {
      const songPrompt = trimmed.startsWith('/create-song ') ? trimmed.slice(13).trim() : trimmed.slice(6).trim();
      normalizedMessage = `Generate a full-length song (at least 2 minutes / 120 seconds duration) with this description: ${songPrompt}. Use generate_music with duration=120. Make it a complete song, not a short clip.`;
    } else if (trimmed.startsWith('/sound ')) {
      normalizedMessage = `Generate a sound effect or music clip for this prompt: ${trimmed.slice(7).trim()}`;
    } else if (trimmed.startsWith('/voice ')) {
      normalizedMessage = `Generate voice audio speaking this text: ${trimmed.slice(7).trim()}`;
    } else if (trimmed.startsWith('/create-skill')) {
      const skillIdea = trimmed.replace(/^\/create-skills?\s*/, '').trim();
      normalizedMessage = skillIdea
        ? `Create a new skill called "${skillIdea}". Step 1: research the topic using web_search (2-3 searches) and web_scrape (1-2 relevant URLs). Step 2: call create_skill with a comprehensive SKILL.md. The SKILL.md must include — YAML frontmatter (name, description, version), ## Overview, ## Parameters (every input with type and description), ## Returns (exact output structure), ## Setup / Prerequisites, ## Usage Examples (at least 2 concrete examples with expected outputs). Make the SKILL.md thorough, specific, and production-ready. Do NOT skip the research step.`
        : `I want to create a new custom skill. First, ask me what the skill should do. Then research using web_search (2-3 searches) and web_scrape (1-2 URLs), and call create_skill with a comprehensive, production-ready SKILL.md including YAML frontmatter, ## Overview, ## Parameters, ## Returns, ## Setup / Prerequisites, and at least 2 ## Usage Examples.`;
    } else if (trimmed.startsWith('/skills')) {
      const query = trimmed.replace(/^\/skills\s*/, '');
      setInput('');
      setSkillSearchQuery(query);
      setIsSkillSearchOpen(true);
      return;
    } else if (
      trimmed === '/skilltree' ||
      trimmed === '/tree' ||
      trimmed === '/packs' ||
      trimmed === '/skill-packs' ||
      trimmed === '/skillpack'
    ) {
      setInput('');
      setSkillTreeMode('view');
      setIsSkillTreeOpen(true);
      return;
    } else if (trimmed === '/create-pack' || trimmed === '/new-pack') {
      setInput('');
      setSkillTreeMode('create');
      setIsSkillTreeOpen(true);
      return;
    }

    const msgContent = normalizedMessage || (attachments.length > 0 ? `Attached: ${attachments.map(a => a.name).join(', ')}` : '');
    lastUserMessageRef.current = msgContent; // Track for auto-retry after credential save

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: msgContent,
      createdAt: new Date().toISOString(),
      attachment: attachments[0] ?? undefined,
      attachments: attachments.length > 0 ? attachments : undefined,
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    // Save to input history (deduplicate consecutive identical entries)
    if (trimmed) {
      const history = inputHistoryRef.current;
      if (history[0] !== trimmed) {
        const newHistory = [trimmed, ...history].slice(0, MAX_INPUT_HISTORY);
        inputHistoryRef.current = newHistory;
        saveInputHistory(newHistory, taskId);
      }
      historyIndexRef.current = -1;
      draftInputRef.current = '';
    }
    const sentAttachments = [...attachments];
    const sentAttachment = sentAttachments[0] || null;
    const totalSize = sentAttachments.reduce((sum, a) => sum + (a.content?.length || 0), 0);
    console.log(`[TaskingBot] Sending ${sentAttachments.length} attachment(s) (${Math.round(totalSize / 1024)}KB total):`, sentAttachments.map(a => `${a.name} (${a.type}, ${Math.round((a.content?.length || 0) / 1024)}KB)`));
    // Warn if attachments are very large
    if (totalSize > 10 * 1024 * 1024) {
      console.warn(`[TaskingBot] Large attachment payload: ${Math.round(totalSize / 1024 / 1024)}MB — may be slow or fail`);
    }
    setAttachments([]);

    // Track this message as pending (for parallel request handling)
    const userMessageId = Date.now().toString();
    setPendingMessageIds((prev) => new Set(prev).add(userMessageId));

    const abortController = new AbortController();
    abortRef.current = abortController;
    let accumulated = '';
    (window as any).__credPromptShown = false; // Reset cred prompt flag for new message

    try {
      // Build history from existing messages (exclude system prompt - backend handles that)
      // Limit to last 40 messages (20 turns) to prevent context window overflow
      const MAX_HISTORY = 40;
      const historyMessages = updatedMessages.slice(0, -1); // exclude current user msg
      const truncated = historyMessages.slice(-MAX_HISTORY);
      const history = truncated.map((m) => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }));

      // Cloud browser is disabled — don't eagerly show browser viewer.
      // Browser viewer will only appear if the backend actually returns browser session data.

      // Task mode: send to task-messages endpoint instead
      const response = isTaskMode && taskId
        ? await fetch('/_api/taskingbot/task-messages', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ taskId, message: msgContent }),
            signal: abortController.signal,
          })
        : await postTaskingBotChat(
        {
          message: msgContent,
          history: history.length > 0 ? history : undefined,
          // Slash commands are ALWAYS new tasks — tell backend to ignore history
          new_task: /^\s*\/[a-z]/i.test(trimmed) ? true : undefined,
          model: selectedModel || undefined,
          skills: activeSkillsPayload.length > 0 ? activeSkillsPayload : undefined,
          attachment: sentAttachment ?? undefined,
          attachments: sentAttachments.length > 0 ? sentAttachments : undefined,
          conversationId: conversationId || undefined,
          multiAgent:
            multiAgentMode !== 'off'
              ? multiAgentMode
              : activeParticipants.length > 0
                ? 'parallel'
                : undefined,
          participants:
            activeParticipants.length > 0 ? activeParticipants.map((p) => p.id) : undefined,
          language: language !== 'en' ? language : undefined,
          channel: agentMode !== 'auto' ? agentMode : undefined,
        },
        { signal: abortController.signal },
      );

      if (!response.ok || !response.body) {
        throw new Error(response.ok ? 'No response body' : `Request failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      const STREAM_TIMEOUT_MS = 300_000; // 5 min max silence — tool calls, media gen, and extension actions cause long pauses
      let lastChunkAt = Date.now();

      // Register this stream globally so it survives component unmount
      const streamKey = getStreamKey(conversationId, taskId);
      _globalStreams.set(streamKey, {
        reader,
        abortController,
        accumulated: '',
        messages: updatedMessages,
        conversationId: conversationId || null,
        isActive: true,
        onChunk: (content: string) => {
          // This callback gets updated by the component when it remounts
          setStreamingContent(content);
        },
      });
      let pageHidden = document.hidden;
      let lastSanitizeAt = 0;
      let sanitizeRafId: number | null = null;

      // Pause timeout when page loses focus (mobile tab switch / app switch).
      // The server continues processing regardless — don't kill the connection.
      const onVisChange = () => {
        pageHidden = document.hidden;
        if (!pageHidden) {
          // Page just became visible — reset the timeout clock so we don't
          // immediately cancel a stream that was healthy before the user left.
          lastChunkAt = Date.now();
        }
      };
      document.addEventListener('visibilitychange', onVisChange);

      try {
        while (true) {
          const readPromise = reader.read();
          const timeoutPromise = new Promise<{ done: true; value: undefined }>((_, reject) => {
            const check = () => {
              // Don't timeout while the page is hidden — the browser throttles
              // background tabs, so the stream may appear stalled when it's fine.
              if (pageHidden) {
                setTimeout(check, 5000);
                return;
              }
              if (Date.now() - lastChunkAt > STREAM_TIMEOUT_MS) {
                reader.cancel();
                reject(new Error('Response timed out — the AI took too long to respond.'));
              } else {
                setTimeout(check, 5000);
              }
            };
            setTimeout(check, STREAM_TIMEOUT_MS);
          });

          const { done, value } = await Promise.race([readPromise, timeoutPromise]);
          if (done) break;

          lastChunkAt = Date.now();
          const chunk = decoder.decode(value, { stream: true });
          accumulated += chunk;

          // Keep global stream state in sync so remounted components can pick up
          const gs = _globalStreams.get(streamKey);
          if (gs) {
            gs.accumulated = accumulated;
            gs.onChunk?.(accumulated);
          }

          // Extract browser session ID — triggers WebSocket stream for live remote viewer
          {
            const SESSION_OPEN = '[BROWSER_SESSION]';
            const SESSION_CLOSE = '[/BROWSER_SESSION]';
            const sIdx = accumulated.indexOf(SESSION_OPEN);
            if (sIdx !== -1) {
              const sStart = sIdx + SESSION_OPEN.length;
              const sEnd = accumulated.indexOf(SESSION_CLOSE, sStart);
              if (sEnd !== -1) {
                const sessionId = accumulated.substring(sStart, sEnd).trim();
                if (sessionId && !browserFramePollRef.current) {
                  console.log('[BrowserViewer] Connecting WebSocket stream for session:', sessionId);
                  setBrowserViewer({ url: '', title: 'Connecting...', screenshot: '', success: true, actions: 0, status: 'Connecting to browser...', sessionId });

                  // Try to get direct Fly.io WebSocket URL for real-time streaming
                  let wsUrl = '';
                  try {
                    const streamInfo = await fetch(`/_api/taskingbot/browser-stream-url?sessionId=${encodeURIComponent(sessionId)}`, { credentials: 'include' });
                    if (streamInfo.ok) {
                      const info = await streamInfo.json() as any;
                      if (info.wsUrl) wsUrl = info.wsUrl;
                    }
                  } catch {}
                  // Fallback to Worker proxy if direct URL unavailable
                  if (!wsUrl) {
                    const wsProtocol = location.protocol === 'https:' ? 'wss' : 'ws';
                    wsUrl = `${wsProtocol}://${location.host}/_api/taskingbot/browser-stream?sessionId=${encodeURIComponent(sessionId)}&fps=3`;
                  }
                  let wsConnected = false;
                  let fallbackPoll: ReturnType<typeof setInterval> | null = null;

                  const startFallbackPolling = () => {
                    if (fallbackPoll) return;
                    console.log('[BrowserViewer] Falling back to HTTP polling');
                    fallbackPoll = setInterval(async () => {
                      try {
                        const res = await fetch(`/_api/taskingbot/browser-frame?sessionId=${encodeURIComponent(sessionId)}`, { cache: 'no-store', credentials: 'include' });
                        if (res.status === 200 && res.headers.get('Content-Type')?.includes('image')) {
                          const blob = await res.blob();
                          if (blob.size > 100) {
                            setBrowserViewer((prev) => {
                              if (prev?.isBlobUrl && prev?.screenshot) URL.revokeObjectURL(prev.screenshot);
                              return prev;
                            });
                            const imgUrl = URL.createObjectURL(blob);
                            const frameUrl = decodeURIComponent(res.headers.get('X-Frame-Url') || '');
                            const frameTitle = decodeURIComponent(res.headers.get('X-Frame-Title') || '');
                            const frameStatus = decodeURIComponent(res.headers.get('X-Frame-Status') || '');
                            setBrowserViewer((prev) => ({
                              url: frameUrl,
                              title: frameTitle,
                              screenshot: imgUrl,
                              success: true,
                              actions: 0,
                              status: frameStatus,
                              isBlobUrl: true,
                              sessionId: prev?.sessionId || sessionId,
                            }));
                          }
                        }
                      } catch (e) {
                        console.error('[BrowserViewer] Poll error:', e);
                      }
                    }, 800);
                  };

                  try {
                    const ws = new WebSocket(wsUrl);
                    browserFramePollRef.current = ws as any; // Store for cleanup
                    browserWsRef.current = ws; // Dedicated WS ref for sending actions

                    ws.onopen = () => {
                      wsConnected = true;
                      setBrowserViewer((prev) => prev ? { ...prev, status: 'Live — VNC mode' } : prev);
                      console.log('[BrowserViewer] WebSocket connected — VNC mode active');
                      // Stop any fallback polling — WS takes over
                      if (fallbackPoll) { clearInterval(fallbackPoll); fallbackPoll = null; }
                    };

                    ws.onmessage = (event) => {
                      try {
                        const msg = JSON.parse(event.data);
                        if (msg.type === 'frame' && msg.image) {
                          const byteString = atob(msg.image);
                          const ab = new ArrayBuffer(byteString.length);
                          const ia = new Uint8Array(ab);
                          for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
                          const blob = new Blob([ab], { type: 'image/jpeg' });
                          setBrowserViewer((prev) => {
                            if (prev?.isBlobUrl && prev?.screenshot) URL.revokeObjectURL(prev.screenshot);
                            return {
                              ...prev,
                              screenshot: URL.createObjectURL(blob),
                              success: true,
                              isBlobUrl: true,
                              sessionId: prev?.sessionId || sessionId,
                            } as any;
                          });
                        } else if (msg.type === 'error') {
                          console.warn('[BrowserViewer] WS error:', msg.error);
                        }
                      } catch {}
                    };

                    ws.onerror = () => {
                      console.warn('[BrowserViewer] WebSocket error, falling back to polling');
                      wsConnected = false;
                      browserWsRef.current = null;
                      startFallbackPolling();
                    };

                    ws.onclose = () => {
                      wsConnected = false;
                      browserWsRef.current = null;
                      console.log('[BrowserViewer] WebSocket closed, starting fallback polling');
                      startFallbackPolling();
                    };

                    // Reconnect WebSocket when tab becomes visible again
                    // Browsers kill WS connections in background tabs after ~30s
                    const reconnectOnVisible = () => {
                      if (document.hidden) return;
                      const currentWs = browserWsRef.current;
                      if (currentWs && currentWs.readyState === WebSocket.OPEN) return; // Still alive
                      console.log('[BrowserViewer] Tab visible — reconnecting WebSocket');
                      try {
                        if (fallbackPoll) { clearInterval(fallbackPoll); fallbackPoll = null; }
                        const newWs = new WebSocket(wsUrl);
                        browserWsRef.current = newWs;
                        browserFramePollRef.current = newWs as any;
                        newWs.onopen = () => {
                          wsConnected = true;
                          setBrowserViewer((prev) => prev ? { ...prev, status: 'Live — VNC mode' } : prev);
                          console.log('[BrowserViewer] WebSocket reconnected');
                        };
                        newWs.onmessage = ws.onmessage;
                        newWs.onerror = () => {
                          wsConnected = false;
                          browserWsRef.current = null;
                          startFallbackPolling();
                        };
                        newWs.onclose = () => {
                          wsConnected = false;
                          browserWsRef.current = null;
                          startFallbackPolling();
                        };
                      } catch {
                        startFallbackPolling();
                      }
                    };
                    document.addEventListener('visibilitychange', reconnectOnVisible);

                    // If WebSocket doesn't connect within 3s, start fallback polling alongside
                    setTimeout(() => {
                      if (!wsConnected) startFallbackPolling();
                    }, 3000);
                  } catch (e) {
                    console.warn('[BrowserViewer] WebSocket failed:', e);
                    browserWsRef.current = null;
                    startFallbackPolling();
                    browserFramePollRef.current = fallbackPoll as any;
                  }
                }
              }
            }
          }
          // Extract browser status updates
          {
            const STATUS_OPEN = '[BROWSER_STATUS]';
            const STATUS_CLOSE = '[/BROWSER_STATUS]';
            const lastStatusStart = accumulated.lastIndexOf(STATUS_OPEN);
            if (lastStatusStart !== -1) {
              const textStart = lastStatusStart + STATUS_OPEN.length;
              const closeIdx = accumulated.indexOf(STATUS_CLOSE, textStart);
              if (closeIdx !== -1) {
                const statusText = accumulated.substring(textStart, closeIdx).trim();
                setBrowserViewer((prev) => prev ? { ...prev, status: statusText } : {
                  url: '', title: '', screenshot: '', success: true, actions: 0, status: statusText,
                });
                // Auto-enable user control when bot asks for verification help
                if (/waiting for you|verification|captcha/i.test(statusText)) {
                  setBrowserUserControl(true);
                }
              }
            }
          }

          // Extract tool scan/exec progress for animated indicators
          {
            const toolProgressMap = new Map<string, 'scanning' | 'passed' | 'running' | 'done' | 'error' | 'blocked'>();
            const scanMatches = accumulated.matchAll(/\[TOOL_SCAN:([^:]+):(passed|blocked)\]/g);
            for (const m of scanMatches) toolProgressMap.set(m[1], m[2] === 'passed' ? 'passed' : 'blocked');
            const execMatches = accumulated.matchAll(/\[TOOL_EXEC:([^:]+):(running|done|error|blocked)\]/g);
            for (const m of execMatches) toolProgressMap.set(m[1], m[2] as any);
            if (toolProgressMap.size > 0) {
              setActiveToolProgress(Array.from(toolProgressMap.entries()).map(([name, status]) => ({ name, status })));
            }
          }

          // Extract desktop actions for UI display
          {
            const daMatch = accumulated.match(/\[DESKTOP_ACTIONS\]([\s\S]*?)\[\/DESKTOP_ACTIONS\]/);
            if (daMatch) {
              try {
                const parsed = JSON.parse(daMatch[1]);
                const actions = (parsed.actions || []).map((a: any) => ({
                  type: a.type || 'unknown',
                  detail: a.text || a.url || a.app || a.command || a.key || a.keys?.join('+') || '',
                  status: 'running' as const,
                }));
                if (actions.length > 0) setActiveDesktopActions(actions);
              } catch {}
            }
            // Also detect from [DESKTOP_ACTION_REPORT] — marks completion
            if (accumulated.includes('[DESKTOP_ACTION_REPORT]')) {
              setActiveDesktopActions(prev => prev.map(a => ({ ...a, status: 'done' as const })));
            }
          }

          // Detect credential prompt DURING streaming — server polls waiting for creds,
          // so we must show the form before the stream finishes
          {
            const credJson = extractCredRequired(accumulated);
            const credMatch = credJson ? [null, credJson] : null;
            if (credMatch && !(window as any).__credPromptShown) {
              (window as any).__credPromptShown = true;
              try {
                const parsed = JSON.parse(credMatch[1]);
                const integrationKey = parsed.integration;
                const predefined = CREDENTIAL_REQUIREMENTS[integrationKey];
                if (parsed.fieldDefs && parsed.fieldDefs.length > 0) {
                  credentialPrompt.openWithFields({
                    integration: integrationKey,
                    integrationLabel: parsed.label || predefined?.label || integrationKey.replace(/[_-]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
                    fields: parsed.fieldDefs.map((f: any) => ({
                      key: f.key,
                      label: f.label,
                      type: f.type === 'url' ? 'text' : f.type || 'password',
                      placeholder: f.placeholder || `Enter ${f.label}`,
                    })),
                    message: `${parsed.label || predefined?.label || integrationKey} credentials needed`,
                  });
                } else if (predefined) {
                  credentialPrompt.handleError({ type: 'MISSING_CREDENTIALS', integration: integrationKey });
                }
              } catch (e) { console.error('[CredPrompt] Parse error during stream:', e); }
            }
          }

          // Update streaming content so user sees progress — throttled to avoid
          // running 20+ regex ops on every chunk (was causing 2500ms+ handler delays)
          const now = performance.now();
          if (now - lastSanitizeAt > 150) {
            lastSanitizeAt = now;
            if (sanitizeRafId) cancelAnimationFrame(sanitizeRafId);
            sanitizeRafId = requestAnimationFrame(() => {
              setStreamingContent(sanitizeAssistantDisplayContent(accumulated));
              sanitizeRafId = null;
            });
          }
        }
      } finally {
        document.removeEventListener('visibilitychange', onVisChange);
        if (sanitizeRafId) cancelAnimationFrame(sanitizeRafId);
      }
      setStreamingContent('');

      // Detect async PDF placeholder — chat_POST.ts prefixes stream with real DB ID
      // so the poller can match the callback-updated row directly by ID.
      let asyncPdfPlaceholderId: string | null = null;
      const asyncPdfMatch = accumulated.match(/^\[PLACEHOLDER_ID:(\d+)\]/);
      if (asyncPdfMatch) {
        asyncPdfPlaceholderId = asyncPdfMatch[1];
        accumulated = accumulated.replace(/^\[PLACEHOLDER_ID:\d+\]/, '');
      }

      // Strip internal markers before displaying to user
      // NOTE: [SECRET_LABELS_NEEDED] is NOT stripped here — kept in stored content
      // so the in-bubble renderer can detect it and show the inline credential form.
      const cleanedContent = sanitizeAssistantDisplayContent(accumulated);

      // Finalize: add assistant message
      const assistantMessage: ChatMessage = {
        id: asyncPdfPlaceholderId || (Date.now() + 1).toString(),
        role: 'assistant',
        content: cleanedContent,
        createdAt: new Date().toISOString(),
      };

      setMessages((prev) => {
        // Prevent duplicate: if last message has same content, don't add again
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && last.content === cleanedContent) return prev;
        return [...prev, assistantMessage];
      });

      // Persist user + assistant messages to DB.
      // Skip for async PDF — chat_POST.ts already persisted both rows.
      if (!asyncPdfPlaceholderId) {
        saveMessagesToDb(
          [
            { role: 'user', content: msgContent, attachment: sentAttachment ?? undefined },
            { role: 'assistant', content: cleanedContent },
          ],
          conversationId,
        );
      }

      // Detect credential errors — structured markers first, then regex fallback
      const credMarkerJson = extractCredRequired(accumulated);
      if (credMarkerJson) {
        try {
          const parsed = JSON.parse(credMarkerJson);
          const integrationKey = parsed.integration;
          const predefined = CREDENTIAL_REQUIREMENTS[integrationKey];

          // Prefer fieldDefs from the marker — server now always includes them
          // This ensures the modal shows the exact number of fields needed
          if (parsed.fieldDefs && parsed.fieldDefs.length > 0) {
            credentialPrompt.openWithFields({
              integration: integrationKey,
              integrationLabel:
                parsed.label ||
                predefined?.label ||
                integrationKey
                  .replace(/[_-]/g, ' ')
                  .replace(/\b\w/g, (c: string) => c.toUpperCase()),
              fields: parsed.fieldDefs.map((f: any) => ({
                key: f.key,
                label: f.label,
                type: f.type === 'url' ? 'text' : f.type || 'password',
                placeholder: f.placeholder || `Enter ${f.label}`,
              })),
              message: `${parsed.label || predefined?.label || integrationKey} credentials needed`,
            });
          } else if (predefined) {
            // Fallback to predefined registry (backward compat for old markers)
            credentialPrompt.handleError({
              type: 'MISSING_CREDENTIALS',
              integration: integrationKey,
              fields: parsed.fields || predefined.fields.map((f: any) => f.key),
              message: `${predefined.label} credentials needed`,
            });
          } else {
            // Custom integration — fetch field definitions from DB
            try {
              const ciRes = await fetch('/_api/taskingbot/custom-integrations', {
                credentials: 'include',
              });
              if (ciRes.ok) {
                const ciText = await ciRes.text();
                let ciData: any;
                try {
                  ciData = (await import('superjson')).default.parse(ciText);
                } catch {
                  ciData = JSON.parse(ciText);
                }
                const customInt = ciData?.integrations?.find(
                  (ci: any) => ci.name === integrationKey,
                );
                if (customInt && customInt.fields?.length > 0) {
                  credentialPrompt.openWithFields({
                    integration: integrationKey,
                    integrationLabel: customInt.label || integrationKey,
                    fields: customInt.fields.map((f: any) => ({
                      key: f.key,
                      label: f.label,
                      type: f.type === 'url' ? 'text' : f.type,
                      placeholder: f.placeholder || `Enter ${f.label}`,
                    })),
                  });
                } else {
                  // Last resort: use field names from marker
                  credentialPrompt.handleError({
                    type: 'MISSING_CREDENTIALS',
                    integration: integrationKey,
                    fields: parsed.fields || ['api_key'],
                    message: `${integrationKey} credentials needed`,
                  });
                }
              }
            } catch {
              /* silently skip custom integration fetch errors */
            }
          }
        } catch {
          /* malformed marker JSON */
        }
      } else {
        // Fallback: regex-based detection
        const credPatterns = [
          /(\w[\w-]+)\s+(?:webhook|token|credential|api[_ ]?key)\s+(?:not configured|not set|missing|required)/i,
          /(?:not configured|missing|required).*?(?:credential|token|api[_ ]?key).*?(\w[\w-]+)/i,
          /⚠️\s*\*?\*?Credentials Required\*?\*?.*?['"](\w[\w-]+)['"]/i,
          /Go to.*?Integrations.*?add.*?(\w[\w-]+)/i,
        ];
        for (const pattern of credPatterns) {
          const match = accumulated.match(pattern);
          if (match) {
            const candidate = match[1]?.toLowerCase();
            const integrationKey = Object.keys(CREDENTIAL_REQUIREMENTS).find(
              (k) => k === candidate || candidate?.includes(k),
            );
            if (integrationKey) {
              credentialPrompt.handleError({
                type: 'MISSING_CREDENTIALS',
                integration: integrationKey,
                fields: CREDENTIAL_REQUIREMENTS[integrationKey].fields.map((f) => f.key),
                message: `${CREDENTIAL_REQUIREMENTS[integrationKey].label} credentials needed`,
              });
              break;
            }
            // Try custom integrations for unknown integration names
            if (candidate && !integrationKey) {
              try {
                const ciRes = await fetch('/_api/taskingbot/custom-integrations', {
                  credentials: 'include',
                });
                if (ciRes.ok) {
                  const ciText = await ciRes.text();
                  let ciData: any;
                  try {
                    ciData = (await import('superjson')).default.parse(ciText);
                  } catch {
                    ciData = JSON.parse(ciText);
                  }
                  const customInt = ciData?.integrations?.find(
                    (ci: any) => ci.name === candidate || ci.name.includes(candidate),
                  );
                  if (customInt && customInt.fields?.length > 0) {
                    credentialPrompt.openWithFields({
                      integration: customInt.name,
                      integrationLabel: customInt.label,
                      fields: customInt.fields.map((f: any) => ({
                        key: f.key,
                        label: f.label,
                        type: f.type === 'url' ? 'text' : f.type,
                        placeholder: f.placeholder || `Enter ${f.label}`,
                      })),
                    });
                    break;
                  }
                }
              } catch {
                /* silently skip */
              }
            }
          }
        }
      }

      // Refresh tasks list after every chat completion — tasks may have been
      // created, updated, or completed via tool calls during the conversation
      queryClient.invalidateQueries({ queryKey: ['taskingbot', 'tasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskingbot', 'projects'] });
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      console.error('[TaskingBotChat] Error:', err);

      // Check if it's a subscription error
      if (err.message && err.message.includes('subscription')) {
        const match = err.message.match(/requires a (\w+) subscription/);
        const requiredTier = match ? match[1] : 'pro';
        setSubscriptionError({
          requiredTier,
          message: err.message,
        });
        return;
      }

      // Stream was interrupted (mobile tab switch, network blip). The server
      // continues processing — try to recover the COMPLETE response from the DB.
      // Always attempt recovery first, even if we have partial content.
      const recovered = await tryRecoverFromServer(conversationId, parseInt(userMessageId, 10));
      if (recovered) {
        const formattedRecovered = sanitizeAssistantDisplayContent(recovered);
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: formattedRecovered,
            createdAt: new Date().toISOString(),
          },
        ]);
      } else if (accumulated && accumulated.trim().length > 0) {
        // Recovery failed but we have partial content — show it without scary error
        const cleanedContent = sanitizeAssistantDisplayContent(accumulated);
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: cleanedContent,
            createdAt: new Date().toISOString(),
          },
        ]);
        saveMessagesToDb(
          [
            { role: 'user', content: msgContent },
            { role: 'assistant', content: cleanedContent },
          ],
          conversationId,
        );
      } else {
        // No partial content and recovery failed — server still processing
        const statusMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: 'The connection was lost while generating your response. Please try again — if this was a document, try a shorter prompt or just the command (e.g. /sow).',
          createdAt: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, statusMessage]);
      }
    } finally {
      // Mark global stream as complete
      const gsKey = getStreamKey(conversationId, taskId);
      const gsFinal = _globalStreams.get(gsKey);
      if (gsFinal) {
        gsFinal.isActive = false;
        gsFinal.reader = null;
        // Keep accumulated content for 10s so remounting component can grab final result
        setTimeout(() => _globalStreams.delete(gsKey), 10000);
      }
      setStreamingContent('');
      // Remove this message from pending set
      setPendingMessageIds((prev) => {
        const updated = new Set(prev);
        updated.delete(userMessageId);
        return updated;
      });
      abortRef.current = null;
      // Stop frame polling, close WebSocket, and clear browser viewer after stream ends
      if (browserFramePollRef.current) {
        const ref = browserFramePollRef.current as any;
        if (ref && typeof ref.close === 'function') ref.close(); // Close WebSocket
        else clearInterval(ref); // Clear polling interval
        browserFramePollRef.current = null;
      }
      if (browserWsRef.current) {
        try { browserWsRef.current.close(); } catch {}
        browserWsRef.current = null;
      }
      setBrowserUserControl(false);
      setBrowserZoom(1);
      setActiveToolProgress([]);
      setActiveDesktopActions([]);
      if (browserViewer?.screenshot) {
        setTimeout(() => {
          // Revoke blob URL to free memory
          if (browserViewer?.isBlobUrl && browserViewer?.screenshot) {
            URL.revokeObjectURL(browserViewer.screenshot);
          }
          setBrowserViewer(null);
        }, 30000);
      }

      // Process queued messages (sent while this stream was active)
      if (queuedMessagesRef.current.length > 0) {
        const nextMessage = queuedMessagesRef.current.shift()!;
        // Small delay to let state settle before sending next
        // skipUserBubble: queued messages already have their bubble shown
        setTimeout(() => void sendMessageText(nextMessage, { skipUserBubble: true }), 200);
      }
    }
  }, [
    input,
    messages,
    multiAgentMode,
    selectedModel,
    subscriptionError,
    activeParticipants,
    allSlashCommands,
    isTaskMode,
    taskId,
    agentMode,
  ]);

  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+R: toggle history recall search
    if (e.key === 'r' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (recallMode) {
        // Already in recall — cycle to next result
        if (recallResults.length > 0) {
          const next = (recallSelectedIdx + 1) % recallResults.length;
          setRecallSelectedIdx(next);
          setInput(recallResults[next]);
        }
      } else {
        // Enter recall mode
        setRecallMode(true);
        setRecallQuery('');
        setRecallResults(inputHistoryRef.current.slice(0, 20));
        setRecallSelectedIdx(0);
        if (inputHistoryRef.current.length > 0) {
          setInput(inputHistoryRef.current[0]);
        }
      }
      return;
    }

    // Recall mode key handling
    if (recallMode) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setRecallMode(false);
        setRecallQuery('');
        setRecallResults([]);
        setInput(draftInputRef.current || '');
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        setRecallMode(false);
        setRecallQuery('');
        setRecallResults([]);
        // Keep the selected input — user can now send it
        return;
      }
      // Filter history as user types
      if (e.key === 'Backspace') {
        const newQ = recallQuery.slice(0, -1);
        setRecallQuery(newQ);
        const filtered = newQ
          ? inputHistoryRef.current.filter((h) => h.toLowerCase().includes(newQ.toLowerCase()))
          : inputHistoryRef.current.slice(0, 20);
        setRecallResults(filtered);
        setRecallSelectedIdx(0);
        if (filtered.length > 0) setInput(filtered[0]);
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        const newQ = recallQuery + e.key;
        setRecallQuery(newQ);
        const filtered = inputHistoryRef.current.filter((h) => h.toLowerCase().includes(newQ.toLowerCase()));
        setRecallResults(filtered);
        setRecallSelectedIdx(0);
        if (filtered.length > 0) setInput(filtered[0]);
        else setInput(`(no match for "${newQ}")`);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (recallResults.length > 0) {
          const prev = (recallSelectedIdx - 1 + recallResults.length) % recallResults.length;
          setRecallSelectedIdx(prev);
          setInput(recallResults[prev]);
        }
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (recallResults.length > 0) {
          const next = (recallSelectedIdx + 1) % recallResults.length;
          setRecallSelectedIdx(next);
          setInput(recallResults[next]);
        }
        return;
      }
      return;
    }

    // Slash command menu navigation
    if (slashSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashSelectedIdx((prev) => (prev + 1) % slashSuggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashSelectedIdx((prev) => (prev - 1 + slashSuggestions.length) % slashSuggestions.length);
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault();
        const selected = slashSuggestions[slashSelectedIdx];
        if (selected) {
          setInput(selected.cmd + (selected.args ? ' ' : ''));
          setSlashSuggestions([]);
          setSlashSelectedIdx(0);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSlashSuggestions([]);
        setSlashSelectedIdx(0);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() || attachments.length > 0) sendMessage();
      return;
    }

    // ↑ Arrow: navigate backward through history (only on single-line or at start of textarea)
    if (e.key === 'ArrowUp' && !e.shiftKey) {
      const ta = e.currentTarget;
      const atTop = ta.selectionStart === 0 || ta.value.indexOf('\n') === -1;
      if (!atTop) return; // let cursor move normally in multi-line
      const history = inputHistoryRef.current;
      if (history.length === 0) return;
      e.preventDefault();
      const newIdx = Math.min(historyIndexRef.current + 1, history.length - 1);
      if (historyIndexRef.current === -1) draftInputRef.current = input; // save draft
      historyIndexRef.current = newIdx;
      setInput(history[newIdx]);
      // Move cursor to end after update
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          const len = textareaRef.current.value.length;
          textareaRef.current.setSelectionRange(len, len);
        }
      });
      return;
    }

    // ↓ Arrow: navigate forward through history
    if (e.key === 'ArrowDown' && !e.shiftKey) {
      const ta = e.currentTarget;
      const atBottom = ta.selectionStart === ta.value.length || ta.value.indexOf('\n') === -1;
      if (!atBottom) return;
      if (historyIndexRef.current === -1) return; // already at draft
      e.preventDefault();
      const newIdx = historyIndexRef.current - 1;
      if (newIdx < 0) {
        // Back to draft
        historyIndexRef.current = -1;
        setInput(draftInputRef.current);
      } else {
        historyIndexRef.current = newIdx;
        setInput(inputHistoryRef.current[newIdx]);
      }
      return;
    }
  };

  const stopGeneration = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setPendingMessageIds(new Set());
    setStreamingContent('');
  }, []);

  const clearConversation = () => {
    // Abort any pending requests
    if (abortRef.current) {
      abortRef.current.abort();
    }
    setMessages([]);
    setPendingMessageIds(new Set());
    localStorage.removeItem(STORAGE_KEY);
    clearChatHistory(conversationId ?? undefined).catch(() => {});
  };

  /** After credentials are saved, auto-retry the last user message so TaskingBot continues the task. */
  const handleCredentialSaved = useCallback(
    (integration: string) => {
      const lastMsg = lastUserMessageRef.current;
      if (lastMsg) {
        console.log(
          '[TaskingBot] Credentials saved for',
          integration,
          '— auto-retrying last message',
        );
        // Small delay to ensure credentials are flushed to DB before retry
        setTimeout(() => {
          sendMessageText(lastMsg);
        }, 500);
      }
    },
    [sendMessageText],
  );

  // Memoize rendered messages so typing in the input doesn't re-render the entire message list
  const renderedMessages = useMemo(() => {
    const filtered = deferredMessages.filter((m) => m && m.role);
    return filtered.map((msg, msgIdx) => (
      <div
        key={msg.id}
        className={`${styles.message} ${
          msg.role === 'user' ? styles.userMessage : styles.assistantMessage
        }`}
      >
        <div
          className={`${styles.messageAvatar} ${
            msg.role === 'user'
              ? styles.userAvatar
              : `${styles.assistantAvatar} ${styles.assistantAvatarPortal}`
          }`}
        >
          {msg.role === 'user' ? (
            authState.type === 'authenticated' ? (
              <UserAvatar user={authState.user} size="sm" showStatus={true} />
            ) : (
              <UserAvatar user={{ displayName: 'User' }} size="sm" showStatus={false} />
            )
          ) : (
            <TaskingBotAvatarPortal size="sm" alt="TaskingBot" />
          )}
        </div>
        <div
          className={`${styles.messageBubble} ${
            msg.role === 'user' ? styles.userBubble : styles.assistantBubble
          }`}
        >
          {/* Render all attachments (images as thumbnails, docs as file badges) */}
          {(() => {
            const allAtt = msg.attachments?.length ? msg.attachments : msg.attachment ? [msg.attachment] : [];
            if (allAtt.length === 0) return null;
            const isImg = (a: any) => a.type?.startsWith('image/') || a.content?.startsWith('data:image/') || a.url?.includes('/generated/') || /\.(png|jpe?g|gif|webp|svg|ico|bmp|avif)$/i.test(a.name || '');
            const images = allAtt.filter(a => isImg(a) && (a.content || a.url));
            const imgBadges = allAtt.filter(a => isImg(a) && !a.content && !a.url);
            const docs = allAtt.filter(a => !isImg(a));
            return (
              <>
                {images.length > 0 && (
                  <div className={styles.msgAttachImageGrid}>
                    {images.map((img, i) => (
                      <img key={i} src={img.content || img.url} alt={img.name} className={styles.msgAttachImage} title={img.name} />
                    ))}
                  </div>
                )}
                {(docs.length > 0 || imgBadges.length > 0) && (
                  <div className={styles.msgAttachDocsWrap}>
                    {[...imgBadges, ...docs].map((att, i) => (
                      att.url ? (
                        <a key={i} href={att.url} target="_blank" rel="noopener noreferrer" className={styles.msgAttachFile} style={{ textDecoration: 'none' }}>
                          <FileText size={14} />
                          <span>{att.name}</span>
                        </a>
                      ) : (
                        <div key={i} className={styles.msgAttachFile}>
                          <FileText size={14} />
                          <span>{att.name}</span>
                        </div>
                      )
                    ))}
                  </div>
                )}
              </>
            );
          })()}
          {/* Security scan badges rendered after content below */}
          {msg.role === 'assistant' && msg.content.includes('[SKILL_RESULTS]') ? (
            (() => {
              const match = msg.content.match(/\[SKILL_RESULTS\](.*?)\[\/SKILL_RESULTS\]/s);
              if (match) {
                try {
                  const { skills } = JSON.parse(match[1]);
                  return (
                    <SkillResultsCard
                      skills={skills}
                      onInstall={(skill) => {
                        const installed = loadInstalledSkills();
                        const existing = installed.find((s) => s.name === skill.name);
                        if (existing) {
                          toast.info(`${skill.name} is already installed`);
                          return;
                        }
                        const newSkill = {
                          name: skill.name,
                          fullName:
                            skill.fullName || skill.author
                              ? `${skill.author}/${skill.name}`
                              : skill.name,
                          description: skill.description || '',
                          url: skill.url || '',
                          active: true,
                          installedAt: new Date().toISOString(),
                        };
                        installed.push(newSkill);
                        const INSTALLED_SKILLS_KEY = 'tasking-installed-skills';
                        localStorage.setItem(INSTALLED_SKILLS_KEY, JSON.stringify(installed));
                        toast.success(`${skill.name} installed!`);
                      }}
                    />
                  );
                } catch (e) {
                  return (
                    <RichMessageContent
                      content={msg.content}
                      onRegenerate={sendMessageText}
                      onImageEnhance={handleEnhanceImage} onRemoveBackground={handleRemoveBackground}
                    />
                  );
                }
              }
              return (
                <RichMessageContent content={msg.content} onRegenerate={sendMessageText} onImageEnhance={handleEnhanceImage} onRemoveBackground={handleRemoveBackground} />
              );
            })()
          ) : msg.role === 'assistant' && msg.content.includes('[EXEC_PLAN]') ? (
            (() => {
              const execBlock = parseExecPlan(msg.content);
              if (execBlock) {
                // Parse live step markers from the full content
                const liveStatuses = new Map<number, string>();
                const stepRx = /\[EXEC_STEP:(\d+):(active|done|failed|skipped)\]/g;
                let stepMatch: RegExpExecArray | null;
                while ((stepMatch = stepRx.exec(msg.content)) !== null) {
                  liveStatuses.set(parseInt(stepMatch[1], 10), stepMatch[2]);
                }
                return (
                  <>
                    {execBlock.before && (
                      <RichMessageContent
                        content={execBlock.before}
                        onRegenerate={sendMessageText}
                      />
                    )}
                    <ExecPlanCard
                      data={execBlock.data}
                      liveStepStatuses={liveStatuses.size > 0 ? liveStatuses : undefined}
                      onExecute={() => sendMessageText('Execute the plan.')}
                      onModify={(text) => sendMessageText(`Modify the plan: ${text}`)}
                      onCancel={() => sendMessageText('Cancel the plan.')}
                    />
                    {execBlock.after && (
                      <RichMessageContent
                        content={sanitizeAssistantDisplayContent(
                          execBlock.after
                            .replace(/\[EXEC_STEP:\d+:(?:active|done|failed|skipped)\]/g, '')
                            .replace(/\[EXEC_STEP:complete\]/g, '')
                        )}
                        onRegenerate={sendMessageText}
                      />
                    )}
                  </>
                );
              }
              return (
                <RichMessageContent content={msg.content} onRegenerate={sendMessageText} onImageEnhance={handleEnhanceImage} onRemoveBackground={handleRemoveBackground} />
              );
            })()
          ) : msg.role === 'assistant' && (msg.content.includes('[PLAN_BLOCK]') || /\[PLAN_BLOCK[\s\n]/.test(msg.content)) ? (
            (() => {
              const block = parsePlanBlock(msg.content);
              if (block) {
                return (
                  <>
                    {block.before && (
                      <RichMessageContent
                        content={block.before}
                        onRegenerate={sendMessageText}
                      />
                    )}
                    <PlanCard
                      data={block.data}
                      onSelectOption={(_qid, option) => sendMessageText(option)}
                      onCreateTasks={
                        block.data.status === 'confirmed'
                          ? () => {
                              const stepsList = (block.data.steps || [])
                                .map(
                                  (s, i) =>
                                    `${i + 1}. ${s.title}${s.detail ? ' — ' + s.detail : ''}`,
                                )
                                .join('\n');
                              sendMessageText(
                                `Create tasks from this confirmed plan. Here are the steps:\n${stepsList}\n\nCreate a task for each step using create_task. Use appropriate priorities (first steps = high, later steps = medium/low).`,
                              );
                            }
                          : undefined
                      }
                    />
                    {block.after && (
                      <RichMessageContent
                        content={block.after}
                        onRegenerate={sendMessageText}
                      />
                    )}
                  </>
                );
              }
              return (
                <RichMessageContent content={msg.content} onRegenerate={sendMessageText} onImageEnhance={handleEnhanceImage} onRemoveBackground={handleRemoveBackground} />
              );
            })()
          ) : msg.role === 'assistant' && msg.content.includes('[SECRET_LABELS_NEEDED:') ? (
            (() => {
              const secretMatch = msg.content.match(/\[SECRET_LABELS_NEEDED:([\s\S]*?)\]/);
              const cleanedText = msg.content
                .replace(/\n?\[SECRET_LABELS_NEEDED:[^\]]*\]\n?/g, '')
                .replace(/\n?\[CRED_REQUIRED:[^\]]*\]\n?/g, '')
                .trim();
              if (secretMatch) {
                try {
                  const parsed = JSON.parse(secretMatch[1]);
                  const secretLabels = parsed.labels || [];
                  return (
                    <>
                      {cleanedText && (
                        <RichMessageContent
                          content={cleanedText}
                          onRegenerate={sendMessageText}
                        />
                      )}
                      <SecretLabelsForm
                        labels={secretLabels}
                        integrationLabel={parsed.integrationLabel}
                        onAllSaved={() => {
                          sendMessageText(
                            `[SECRET_SAVED:ALL] All secrets saved. Please retry the last operation.`,
                          );
                        }}
                        onCancel={() => {}}
                      />
                    </>
                  );
                } catch {
                  return (
                    <RichMessageContent
                      content={cleanedText || msg.content}
                      onRegenerate={sendMessageText}
                      onImageEnhance={handleEnhanceImage} onRemoveBackground={handleRemoveBackground}
                    />
                  );
                }
              }
              return (
                <RichMessageContent
                  content={cleanedText || msg.content}
                  onRegenerate={sendMessageText}
                />
              );
            })()
          ) : (
            <RichMessageContent content={msg.content} onRegenerate={sendMessageText} onImageEnhance={handleEnhanceImage} onRemoveBackground={handleRemoveBackground} />
          )}
          {/* Tiny security scan badges — after content, next line */}
          {msg.role === 'assistant' && msg.content.includes('[TOOL_SCAN:') && (() => {
            const scanMatches = [...msg.content.matchAll(/\[TOOL_SCAN:([^:]+):(passed|blocked)\]/g)];
            if (scanMatches.length === 0) return null;
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                {scanMatches.map((m, i) => {
                  const passed = m[2] === 'passed';
                  return (
                    <span key={i} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '3px',
                      fontSize: '0.55rem', fontWeight: 500, padding: '1px 5px',
                      borderRadius: '3px', opacity: 0.55,
                      color: passed ? '#22c55e' : '#ef4444',
                      background: 'transparent',
                    }}>
                      {passed ? <ShieldCheck size={9} /> : <Skull size={9} />}
                      <span style={{ fontFamily: 'inherit' }}>{friendlyToolName(m[1])}</span>
                    </span>
                  );
                })}
              </div>
            );
          })()}
          {msg.role === 'assistant' && (
            <MessageFeedback
              messageId={String(msg.id)}
              source="taskingbot"
              messageContent={msg.content}
              onRetry={() => {
                const prevUserMsg = deferredMessages.slice(0, deferredMessages.indexOf(msg)).reverse().find(m => m.role === 'user');
                if (prevUserMsg) sendMessageText(prevUserMsg.content);
              }}
            />
          )}
        </div>
        {/* Copy handled inside message bubble via mdContentWrap copy button */}
        {/* Timestamp */}
        {msg.createdAt && (
          <div style={{
            fontSize: '0.6rem', color: 'var(--muted-foreground, #64748b)', opacity: 0.6,
            marginTop: 2, paddingLeft: msg.role === 'user' ? 0 : 4,
            textAlign: msg.role === 'user' ? 'right' : 'left',
          }}>
            {new Date(msg.createdAt).toLocaleString('en-US', {
              timeZone: 'America/Chicago',
              hour: 'numeric', minute: '2-digit', hour12: true,
              month: 'short', day: 'numeric',
            })}
          </div>
        )}
      </div>
    ));
  }, [deferredMessages, authState, sendMessageText, handleEnhanceImage, handleRemoveBackground, copiedMsgId, handleCopyMessage]);

  return (
    <>
      <div className={styles.chatLayout}>
        {/* ChatGPT-style conversation sidebar (hidden in task mode — task chats
            are scoped to a single task and don't have separate conversations) */}
        {!isTaskMode && authState.type === 'authenticated' && (
          <ConversationSidebar
            activeId={conversationId}
            projectId={projectId || '__global__'}
            open={sidebarOpen}
            onToggle={() => setSidebarOpen((v) => !v)}
            refreshKey={sidebarRefreshKey}
            onSelect={(id) => {
              setUserPickedConvId(id);
            }}
            onNew={(id) => {
              setUserPickedConvId(id);
              setMessages([]);
              setSidebarRefreshKey((k) => k + 1);
            }}
            onMutated={() => {
              // If the deleted/renamed chat was the active one, drop the override
              setSidebarRefreshKey((k) => k + 1);
            }}
          />
        )}
      <div className={styles.chatContainer}>
        {mainPanelTab === 'assets' ? (
          <ChatAttachmentsLibrary
            sessionId={conversationId || (projectId ? `taskingbot:${projectId}` : 'taskingbot-global')}
            isOpen={true}
            onClose={() => setMainPanelTab('chat')}
            variant="inline"
            onSelectAttachment={(selected) => {
              setAttachment({
                name: selected.fileName,
                type: selected.fileType || 'application/octet-stream',
                content: selected.dataUrl,
                size: selected.fileSize || 0,
              });
              setMainPanelTab('chat');
              toast.success(`Attached ${selected.fileName}`);
            }}
            onImageEnhance={handleEnhanceImage}
            onRemoveBackground={handleRemoveBackground}
            onSendToChat={(selected) => {
              setAttachment({
                name: selected.fileName,
                type: selected.fileType || 'application/octet-stream',
                content: selected.dataUrl,
                size: selected.fileSize || 0,
              });
              setMainPanelTab('chat');
              toast.success(`Sent ${selected.fileName} to chat`);
            }}
          />
        ) : (
          <>
        <div className={styles.messagesArea}>
          {subscriptionError ? (
            <div className={styles.subscriptionPrompt}>
              <div className={styles.subscriptionIcon}>
                <Bot size={48} />
              </div>
              <h3 className={styles.subscriptionTitle}>{t.taskingbot.proFeature}</h3>
              <p className={styles.subscriptionMessage}>
                {t.taskingbot.requiresSubscription.replace('{tier}', subscriptionError.requiredTier)}
              </p>
              <div className={styles.subscriptionFeatures}>
                <div className={styles.feature}>✨ {t.taskingbot.intelligentAgent}</div>
                <div className={styles.feature}>
                  🔗 {t.taskingbot.multiChannel}
                </div>
                <div className={styles.feature}>🚀 {t.taskingbot.taskAutomation}</div>
                <div className={styles.feature}>📅 {t.taskingbot.calendarManagement}</div>
              </div>
              <a
                href="/pricing"
                className={styles.upgradeButton}
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/pricing';
                }}
              >
                {t.taskingbot.upgradeToPro}
              </a>
              <button className={styles.dismissLink} onClick={() => setSubscriptionError(null)}>
                {t.taskingbot.dismissTryAgain}
              </button>
            </div>
          ) : messages.length === 0 && pendingMessageIds.size === 0 ? (
            <div className={styles.welcomeMessage}>
              <div className={styles.welcomeIcon}>
                <TaskingBotAvatarPortal
                  size="md"
                  className={styles.welcomePortal}
                  alt="TaskingBot"
                />
              </div>
              <h3 className={styles.welcomeTitle}>{t.taskingbot.title}</h3>
              <p className={styles.welcomeSubtitle}>
                {t.taskingbot.welcome}
              </p>
            </div>
          ) : null}

          {renderedMessages}

          {/* Async PDF tool badges — render when activeToolProgress has items
              and there's no active streaming message (since the streaming
              block has its own badge row). */}
          {activeToolProgress.length > 0 && pendingMessageIds.size === 0 && (
            <div className={`${styles.message} ${styles.assistantMessage}`}>
              <div className={`${styles.messageAvatar} ${styles.assistantAvatar}`}>
                <TaskingBotAvatarPortal size="sm" alt="TaskingBot" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 0%', minWidth: 0 }}>
                <div className={styles.toolProgressRow}>
                  {activeToolProgress.map((tp, i) => (
                    <span key={`async-${tp.name}-${i}`} className={`${styles.toolProgressBadge} ${styles[`toolProgress_${tp.status}`] || ''}`}>
                      {tp.status === 'running' ? (
                        ['web_scrape', 'scrape_url', 'http_get', 'browse_page', 'web_search', 'web_fetch', 'browse'].includes(tp.name)
                          ? <Globe size={10} />
                          : <span className={styles.toolProgressSpinner} />
                      ) : tp.status === 'passed' || tp.status === 'done' ? (
                        <ShieldCheck size={10} />
                      ) : tp.status === 'blocked' || tp.status === 'error' ? (
                        <Skull size={10} />
                      ) : (
                        <span className={styles.toolProgressSpinner} />
                      )}
                      <span className={styles.toolProgressName}>{friendlyToolName(tp.name)}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {pendingMessageIds.size > 0 && (
            <div className={`${styles.message} ${styles.assistantMessage}`}>
              <div className={`${styles.messageAvatar} ${styles.assistantAvatar}`}>
                <TaskingBotAvatarPortal size="sm" alt="TaskingBot" />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 0%', minWidth: 0 }}>
                {/* Cloud Browser Viewer — inline screenshare */}
                {browserViewer && browserViewer.screenshot && (
                  <>
                    {browserViewerExpanded && (
                      <div className={styles.browserViewerBackdrop} onClick={() => setBrowserViewerExpanded(false)} />
                    )}
                    <div className={`${styles.browserViewer} ${browserViewerExpanded ? styles.browserViewerExpanded : ''}`}>
                      <div className={styles.browserViewerHeader}>
                        <div className={styles.browserViewerDots}>
                          <span className={styles.dot} style={{ background: '#ff5f57' }} />
                          <span className={styles.dot} style={{ background: '#febc2e' }} />
                          <span className={styles.dot} style={{ background: '#28c840' }} />
                        </div>
                        <div className={styles.browserViewerUrl}>
                          {browserViewer.url || 'Browsing...'}
                        </div>
                        {/* Zoom toggle */}
                        <button
                          className={styles.browserViewerExpandBtn}
                          onClick={() => setBrowserZoom((z) => z === 1 ? 2 : z === 2 ? 4 : 1)}
                          title={`Zoom: ${browserZoom}x`}
                        >
                          {browserZoom}x
                        </button>
                        {/* Open in new window */}
                        <button
                          className={styles.browserViewerExpandBtn}
                          onClick={() => {
                            const imgSrc = browserViewer.isBlobUrl ? browserViewer.screenshot : `data:image/jpeg;base64,${browserViewer.screenshot}`;
                            const win = window.open('', '_blank', 'width=1400,height=900');
                            if (win) {
                              win.document.title = browserViewer.title || 'Browser Viewer';
                              win.document.body.style.cssText = 'margin:0;background:#111;display:flex;align-items:center;justify-content:center;height:100vh';
                              const img = win.document.createElement('img');
                              img.src = imgSrc;
                              img.style.cssText = 'max-width:100%;max-height:100%;object-fit:contain';
                              win.document.body.appendChild(img);
                              // Auto-refresh
                              const sid = browserViewer.sessionId;
                              if (sid) {
                                const iv = setInterval(async () => {
                                  try {
                                    if (win.closed) { clearInterval(iv); return; }
                                    const res = await fetch(`/_api/taskingbot/browser-frame?sessionId=${encodeURIComponent(sid)}`, { cache: 'no-store', credentials: 'include' });
                                    if (res.ok && res.headers.get('Content-Type')?.includes('image')) {
                                      const blob = await res.blob();
                                      if (blob.size > 100) img.src = URL.createObjectURL(blob);
                                    }
                                  } catch {}
                                }, 800);
                              }
                            }
                          }}
                          title="Open in new window"
                        >
                          ↗
                        </button>
                        {/* Expand/collapse */}
                        <button
                          className={styles.browserViewerExpandBtn}
                          onClick={() => setBrowserViewerExpanded((p) => !p)}
                          title={browserViewerExpanded ? 'Collapse' : 'Expand'}
                        >
                          {browserViewerExpanded ? '⊖' : '⊕'}
                        </button>
                      </div>
                      <div
                        className={`${styles.browserViewerScreen} ${styles.browserViewerInteractive}`}
                        onClick={browserViewer.sessionId ? (e) => {
                          // Use currentTarget (the screen div) for reliable coordinate calculation
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          // Account for letterboxing: image is object-fit:contain inside the container
                          // The actual image area may be smaller than the container
                          const imgEl = (e.currentTarget as HTMLElement).querySelector('img');
                          let x = (e.clientX - rect.left) / rect.width;
                          let y = (e.clientY - rect.top) / rect.height;
                          if (imgEl) {
                            // Calculate the actual rendered image bounds within the container
                            const containerAR = rect.width / rect.height;
                            const imageAR = 1280 / 900; // Browser viewport aspect ratio
                            let imgW = rect.width, imgH = rect.height, imgX = 0, imgY = 0;
                            if (containerAR > imageAR) {
                              // Container wider than image — letterboxed left/right
                              imgH = rect.height;
                              imgW = imgH * imageAR;
                              imgX = (rect.width - imgW) / 2;
                            } else {
                              // Container taller than image — letterboxed top/bottom
                              imgW = rect.width;
                              imgH = imgW / imageAR;
                              imgY = (rect.height - imgH) / 2;
                            }
                            x = (e.clientX - rect.left - imgX) / imgW;
                            y = (e.clientY - rect.top - imgY) / imgH;
                          }
                          // Clamp to valid range
                          x = Math.max(0, Math.min(1, x));
                          y = Math.max(0, Math.min(1, y));
                          sendBrowserAction(browserViewer.sessionId!, { action: 'click', x, y });
                        } : undefined}
                        tabIndex={browserViewer.sessionId ? 0 : undefined}
                        onKeyDown={browserViewer.sessionId ? (e) => {
                          // Forward keyboard events to the remote browser
                          // Don't forward if user is typing in the text input below
                          if ((e.target as HTMLElement).tagName === 'INPUT') return;
                          e.preventDefault();
                          const key = e.key;
                          if (key.length === 1) {
                            // Printable character — send as type
                            sendBrowserAction(browserViewer.sessionId!, { action: 'type', text: key });
                          } else {
                            // Special key (Enter, Tab, Escape, Arrow keys, etc.)
                            sendBrowserAction(browserViewer.sessionId!, { action: 'keypress', key });
                          }
                        } : undefined}
                        onWheel={browserViewer.sessionId ? (e) => {
                          const now = Date.now();
                          if (now - browserScrollThrottle.current < 100) return;
                          browserScrollThrottle.current = now;
                          sendBrowserAction(browserViewer.sessionId!, { action: 'scroll', deltaX: e.deltaX, deltaY: e.deltaY });
                        } : undefined}
                      >
                        <img
                          src={browserViewer.isBlobUrl ? browserViewer.screenshot : `data:image/jpeg;base64,${browserViewer.screenshot}`}
                          alt={browserViewer.title || 'Browser screenshot'}
                          className={styles.browserViewerImg}
                          style={browserZoom > 1 ? { transform: `scale(${browserZoom})`, transformOrigin: 'top left', width: '100%', height: '100%' } : undefined}
                          draggable={false}
                        />
                      </div>
                      {/* Text input — always visible so user can type anytime */}
                      {browserViewer.sessionId && (
                        <div className={styles.browserViewerInputRow}>
                          <input
                            className={styles.browserViewerInput}
                            type="text"
                            placeholder="Type here..."
                            value={browserInputText}
                            onChange={(e) => setBrowserInputText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && browserInputText.trim()) {
                                const typedText = browserInputText;
                                sendBrowserAction(browserViewer.sessionId!, { action: 'type', text: typedText });
                                setBrowserInputText('');
                              } else if (['Tab', 'Escape'].includes(e.key)) {
                                e.preventDefault();
                                sendBrowserAction(browserViewer.sessionId!, { action: 'keypress', key: e.key });
                              }
                            }}
                          />
                          <button
                            className={styles.browserViewerKeyBtn}
                            onClick={() => sendBrowserAction(browserViewer.sessionId!, { action: 'keypress', key: 'Enter' })}
                          >
                            ↵
                          </button>
                          <button
                            className={styles.browserViewerKeyBtn}
                            onClick={() => sendBrowserAction(browserViewer.sessionId!, { action: 'keypress', key: 'Tab' })}
                          >
                            Tab
                          </button>
                        </div>
                      )}
                      <div className={styles.browserViewerFooter}>
                        <span className={styles.browserViewerLive}>LIVE</span>
                        <span>{browserViewer.title || 'Loading...'}</span>
                        {browserViewer.status && <span style={{ marginLeft: 'auto', opacity: 0.7, fontFamily: 'monospace', fontSize: '0.6rem' }}>{browserViewer.status}</span>}
                      </div>
                    </div>
                  </>
                )}
                {browserViewer && !browserViewer.screenshot && (
                  <div className={styles.browserViewer}>
                    <div className={styles.browserViewerHeader}>
                      <div className={styles.browserViewerDots}>
                        <span className={styles.dot} style={{ background: '#ff5f57' }} />
                        <span className={styles.dot} style={{ background: '#febc2e' }} />
                        <span className={styles.dot} style={{ background: '#28c840' }} />
                      </div>
                      <div className={styles.browserViewerUrl}>
                        {browserViewer.url || 'Connecting...'}
                      </div>
                    </div>
                    <div className={styles.browserViewerScreen} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                      <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🌐</div>
                        <div style={{ fontSize: '0.75rem' }}>{browserViewer.status || 'Starting browser...'}</div>
                      </div>
                    </div>
                    <div className={styles.browserViewerFooter}>
                      <span className={styles.browserViewerLive}>LIVE</span>
                      <span>{browserViewer.status || 'Connecting...'}</span>
                    </div>
                  </div>
                )}
                {/* Tool scan/exec progress indicators */}
                {activeToolProgress.length > 0 && (
                  <div className={styles.toolProgressRow}>
                    {activeToolProgress.map((tp, i) => (
                      <span key={`${tp.name}-${i}`} className={`${styles.toolProgressBadge} ${styles[`toolProgress_${tp.status}`] || ''}`}>
                        {tp.status === 'running' ? (
                          ['web_scrape', 'scrape_url', 'http_get', 'browse_page', 'web_search', 'web_fetch', 'browse'].includes(tp.name)
                            ? <Globe size={10} />
                            : <span className={styles.toolProgressSpinner} />
                        ) : tp.status === 'passed' || tp.status === 'done' ? (
                          <ShieldCheck size={10} />
                        ) : tp.status === 'blocked' || tp.status === 'error' ? (
                          <Skull size={10} />
                        ) : (
                          <span className={styles.toolProgressSpinner} />
                        )}
                        <span className={styles.toolProgressName}>{friendlyToolName(tp.name)}</span>
                      </span>
                    ))}
                  </div>
                )}
                {streamingContent ? (
                  <div className={`${styles.messageBubble} ${styles.assistantBubble}`}>
                    <RichMessageContent content={streamingContent} onRegenerate={sendMessageText} onImageEnhance={handleEnhanceImage} onRemoveBackground={handleRemoveBackground} />
                  </div>
                ) : browserViewer || agentMode === 'cloud_browser' ? (
                  <div className={`${styles.messageBubble} ${styles.assistantBubble}`} style={{ fontSize: '0.8rem', opacity: 0.7, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <BrowserIcon size={14} />
                    <span>{browserViewer?.status || (browserViewer?.screenshot ? 'Browsing...' : 'Starting cloud browser...')}</span>
                  </div>
                ) : agentMode === 'extension' ? (
                  <div className={`${styles.messageBubble} ${styles.assistantBubble}`} style={{ fontSize: '0.8rem', opacity: 0.7, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Puzzle size={14} />
                    <span>Working via extension...</span>
                  </div>
                ) : agentMode === 'desktop' ? (
                  <div className={`${styles.messageBubble} ${styles.assistantBubble}`} style={{ fontSize: '0.8rem', opacity: 0.85 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: activeDesktopActions.length > 0 ? 6 : 0 }}>
                      <Monitor size={14} />
                      <span style={{ fontWeight: 600 }}>Desktop Agent</span>
                    </div>
                    {activeDesktopActions.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {activeDesktopActions.map((action, i) => {
                          const icons: Record<string, string> = {
                            screenshot: '📸', mouse_click: '🖱️', keyboard_type: '⌨️', keyboard_hotkey: '⌨️',
                            open_app: '🚀', run_command: '💻', navigate: '🌐', click: '🖱️', type: '⌨️',
                            wait: '⏳', ocr_screen: '👁️', window_focus: '🪟', file_read: '📄', file_write: '💾',
                          };
                          const icon = icons[action.type] || '⚡';
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.75rem', opacity: action.status === 'done' ? 0.5 : 1 }}>
                              <span>{icon}</span>
                              <span style={{ color: 'var(--foreground)' }}>{action.type.replace(/_/g, ' ')}</span>
                              {action.detail && <span style={{ color: 'var(--muted-foreground)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{action.detail}</span>}
                              {action.status === 'done' ? <span style={{ color: 'var(--success, #22c55e)' }}>✓</span> : <span style={{ animation: 'pulse 1s infinite', color: 'var(--primary)' }}>●</span>}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span style={{ opacity: 0.6 }}>Working on desktop...</span>
                    )}
                  </div>
                ) : (
                  <div
                    className={`${styles.messageBubble} ${styles.assistantBubble} ${styles.thinkingBubble}`}
                  >
                    <div className={styles.thinkingIconStack}>
                      <Lightbulb size={16} className={styles.thinkingLightbulb} />
                      <Brain size={18} className={styles.thinkingBrain} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Secret labels forms now render inline inside message bubbles */}

          <div ref={messagesEndRef} />
        </div>

        {/* Dynamic suggestion chips based on user's recent activity */}
        {messages.length === 0 && !streamingContent && dynamicSuggestions.length > 0 && (
          <div className={styles.suggestionChips}>
            {dynamicSuggestions.map((s, idx) => (
              <button
                key={`${s.label}-${idx}`}
                className={styles.suggestionChip}
                onClick={() => {
                  setInput(s.prompt);
                  textareaRef.current?.focus();
                }}
              >
                {s.icon && <span className={styles.suggestionChipIcon}>{s.icon}</span>}
                {s.label}
              </button>
            ))}
          </div>
        )}

        <div className={styles.inputArea}>
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.json,.js,.ts,.py,.html,.css,.xml,.yaml,.yml,.sql,.sh,.log"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />

          {/* Attachment preview grid */}
          {attachments.length > 0 && (
            <div className={styles.attachmentGrid}>
              {attachments.map((att, idx) => {
                const isImage = att.type?.startsWith('image/') || att.content?.startsWith('data:image/') || /\.(png|jpe?g|gif|webp|svg|ico|bmp|avif)$/i.test(att.name || '');
                const isPdf = att.type === 'application/pdf' || /\.pdf$/i.test(att.name || '');
                const isDoc = /\.(docx?|xlsx?|pptx?)$/i.test(att.name || '');
                return (
                  <div key={`${att.name}-${idx}`} className={styles.attachmentCard}>
                    <button
                      className={styles.attachmentRemoveCorner}
                      onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                      title="Remove"
                    >
                      <X size={10} />
                    </button>
                    {isImage ? (
                      <img src={att.content} alt={att.name} className={styles.attachmentCardThumb} />
                    ) : (
                      <div className={styles.attachmentCardIcon}>
                        {isPdf ? (
                          <FileText size={22} style={{ color: '#ef4444' }} />
                        ) : isDoc ? (
                          <FileText size={22} style={{ color: '#3b82f6' }} />
                        ) : (
                          <FileText size={22} />
                        )}
                      </div>
                    )}
                    <span className={styles.attachmentCardName}>{att.name}</span>
                  </div>
                );
              })}
            </div>
          )}

          {recallMode && (
            <div className={styles.slashMenu} style={{ borderLeft: '2px solid var(--primary)', padding: '4px 8px', fontSize: '0.75rem', color: 'var(--muted-foreground)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <span style={{ color: 'var(--primary)', fontWeight: 700 }}>recall:</span>
                <span>{recallQuery || '(type to search history)'}</span>
                <span style={{ marginLeft: 'auto', opacity: 0.5 }}>{recallResults.length} match{recallResults.length !== 1 ? 'es' : ''} · Ctrl+R next · Enter select · Esc cancel</span>
              </div>
              {recallResults.slice(0, 8).map((item, idx) => (
                <button
                  key={idx}
                  className={`${styles.slashMenuItem}${idx === recallSelectedIdx ? ` ${styles.slashMenuItemSelected}` : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setInput(item);
                    setRecallMode(false);
                    setRecallQuery('');
                    setRecallResults([]);
                  }}
                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {item.length > 80 ? item.slice(0, 80) + '...' : item}
                </button>
              ))}
            </div>
          )}

          {slashSuggestions.length > 0 && (
            <div className={styles.slashMenu}>
              {slashSuggestions.map((s, idx) => (
                <button
                  key={s.cmd}
                  className={`${styles.slashMenuItem}${idx === slashSelectedIdx ? ` ${styles.slashMenuItemSelected}` : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setInput(s.cmd + (s.args ? ' ' : ''));
                    setSlashSuggestions([]);
                    setSlashSelectedIdx(0);
                  }}
                  onMouseEnter={() => setSlashSelectedIdx(idx)}
                >
                  <span className={styles.slashCmd}>{s.cmd}</span>
                  {s.args && <span className={styles.slashArgs}>{s.args}</span>}
                  <span className={styles.slashDesc}>{s.desc}</span>
                </button>
              ))}
            </div>
          )}

          <div className={styles.inputRow}>
            <div className={styles.inputWrapper}>
              <textarea
                ref={textareaRef}
                id="taskingbot-chat-input"
                name="taskingbot-chat-input"
                className={`${styles.textarea} ${isDragOver ? styles.dragOverInput : ''}`}
                value={input}
                onChange={(e) => {
                  const val = e.target.value;
                  setInput(val);
                  // Reset history navigation when user manually edits
                  if (historyIndexRef.current !== -1) {
                    historyIndexRef.current = -1;
                    draftInputRef.current = val;
                  }
                  if (val.startsWith('/') && !val.includes(' ')) {
                    const filtered = allSlashCommands.filter((c) => c.cmd.startsWith(val));
                    setSlashSuggestions(filtered);
                    setSlashSelectedIdx(0);
                  } else {
                    setSlashSuggestions([]);
                    setSlashSelectedIdx(0);
                  }
                }}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                placeholder="Message TaskingBot... (type / for commands, paste images, or drag & drop files)"
                rows={1}
              />
              {/* History navigation arrows for mobile */}
              {inputHistoryRef.current.length > 0 && (
                <div className={styles.historyArrows}>
                  <button
                    className={`${styles.historyArrow} ${historyIndexRef.current >= inputHistoryRef.current.length - 1 ? styles.historyArrowDisabled : ''}`}
                    onClick={() => {
                      const history = inputHistoryRef.current;
                      if (history.length === 0) return;
                      const newIdx = Math.min(historyIndexRef.current + 1, history.length - 1);
                      if (historyIndexRef.current === -1) draftInputRef.current = input;
                      historyIndexRef.current = newIdx;
                      setInput(history[newIdx]);
                    }}
                    disabled={historyIndexRef.current >= inputHistoryRef.current.length - 1}
                    title="Previous message"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    className={`${styles.historyArrow} ${historyIndexRef.current === -1 ? styles.historyArrowDisabled : ''}`}
                    onClick={() => {
                      if (historyIndexRef.current === -1) return;
                      const newIdx = historyIndexRef.current - 1;
                      if (newIdx < 0) {
                        historyIndexRef.current = -1;
                        setInput(draftInputRef.current);
                      } else {
                        historyIndexRef.current = newIdx;
                        setInput(inputHistoryRef.current[newIdx]);
                      }
                    }}
                    disabled={historyIndexRef.current === -1}
                    title="Next message"
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>
              )}
            </div>
            <div className={styles.inputActions}>
              {/* New chat */}
              <button
                className={styles.attachButton}
                onClick={() => {
                  if (!window.confirm('Are you sure you want to start a new task? Current conversation will be cleared.')) return;
                  setAttachments([]);
                  try { localStorage.removeItem('taskingbot-last-attachment'); } catch {}
                  setMessages([]);
                  localStorage.removeItem(STORAGE_KEY);
                  clearChatHistory(conversationId ?? undefined).catch(() => {});
                  setConversationId(null);
                  setInput('');
                  toast.success('New chat started');
                }}
                title="New chat"
              >
                <SquarePen size={12} />
              </button>
              {/* Attach */}
              <button
                className={styles.attachButton}
                onClick={() => fileInputRef.current?.click()}
                title="Attach file or image"
              >
                <Paperclip size={12} />
              </button>
              {/* Voice output toggle */}
              <button
                className={`${styles.attachButton} ${voiceOutputEnabled ? styles.micActive : ''}`}
                onClick={async () => {
                  const { botVoice } = await import('../helpers/aiBotVoice');
                  if (voiceOutputEnabled) {
                    botVoice.enabled = false;
                    botVoice.stop();
                    setVoiceOutputEnabled(false);
                  } else {
                    await botVoice.init();
                    botVoice.enabled = true;
                    setVoiceOutputEnabled(true);
                  }
                }}
                title={voiceOutputEnabled ? 'Disable voice output' : 'Enable voice output'}
              >
                {voiceOutputEnabled ? <Volume2 size={12} /> : <VolumeX size={12} />}
              </button>
              {isVoiceSupported && (
                <button
                  className={`${styles.attachButton} ${isListening ? styles.micActive : ''}`}
                  onClick={handleVoiceToggle}
                  title={isListening ? 'Stop listening' : 'Start voice input'}
                >
                  {isListening ? <MicOff size={12} /> : <Mic size={12} />}
                </button>
              )}

              {/* Desktop viewer toggle */}
              <button
                className={styles.attachButton}
                onClick={() => {
                  const evt = new CustomEvent('tasking.toggleDesktopViewer');
                  window.dispatchEvent(evt);
                }}
                title="View desktop agent"
              >
                <Monitor size={12} />
              </button>

              {/* Plan / Brainstorm quick actions */}
              <PlanMenu onSelect={(cmd) => {
                setInput(cmd);
                setTimeout(() => textareaRef.current?.focus(), 50);
              }} />

              {/* Schedule button — converts current chat message to a scheduled task */}
              <div style={{ position: 'relative' }}>
                <button
                  className={`${styles.attachButton} ${showSchedulePopup ? styles.micActive : ''}`}
                  onClick={() => setShowSchedulePopup(!showSchedulePopup)}
                  title="Create as scheduled task"
                >
                  <CalendarClock size={12} />
                </button>
                {showSchedulePopup && (
                  <div
                    ref={schedulePopupRef}
                    style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: 0,
                      marginBottom: 6,
                      background: 'var(--background)',
                      border: '1px solid color-mix(in srgb, var(--foreground) 15%, transparent)',
                      borderRadius: 12,
                      padding: '14px 16px',
                      width: 300,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                      zIndex: 50,
                      display: 'flex',
                      flexDirection: 'column' as const,
                      gap: 10,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--foreground)' }}>
                        <CalendarClock size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 6 }} />
                        Schedule Task
                      </div>
                      <button onClick={() => setShowSchedulePopup(false)} style={{ background: 'none', border: 'none', color: 'var(--foreground)', cursor: 'pointer', opacity: 0.5, fontSize: '1rem' }}>&times;</button>
                    </div>

                    {/* Frequency presets */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {([
                        { key: 'once', label: 'One-time', icon: '1' },
                        { key: 'daily', label: 'Daily', icon: '📅' },
                        { key: 'weekdays', label: 'Weekdays', icon: '💼' },
                        { key: 'weekly', label: 'Weekly', icon: '7' },
                        { key: 'biweekly', label: '2x/week', icon: '2' },
                        { key: 'monthly', label: 'Monthly', icon: '📆' },
                        { key: 'custom', label: 'Custom', icon: '⚙' },
                      ] as const).map((opt) => (
                        <button
                          key={opt.key}
                          onClick={() => setScheduleForm((f) => ({
                            ...f,
                            frequency: f.frequency === opt.key ? '' as const : opt.key,
                            selectedDays: opt.key === 'biweekly' ? f.selectedDays : [],
                            customSchedule: opt.key === 'custom' ? f.customSchedule : '',
                          }))}
                          style={{
                            padding: '4px 10px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            borderRadius: 16,
                            border: scheduleForm.frequency === opt.key
                              ? '2px solid var(--primary)'
                              : '1px solid color-mix(in srgb, var(--foreground) 20%, transparent)',
                            background: scheduleForm.frequency === opt.key
                              ? 'color-mix(in srgb, var(--primary) 15%, transparent)'
                              : 'color-mix(in srgb, var(--foreground) 5%, transparent)',
                            color: scheduleForm.frequency === opt.key ? 'var(--primary)' : 'var(--foreground)',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                          }}
                        >
                          <span style={{ marginRight: 3 }}>{opt.icon}</span> {opt.label}
                        </button>
                      ))}
                    </div>

                    {/* Day-of-week selector for weekly / biweekly */}
                    {(scheduleForm.frequency === 'weekly' || scheduleForm.frequency === 'biweekly') && (
                      <div>
                        <div style={{ fontSize: '0.65rem', color: 'color-mix(in srgb, var(--foreground) 60%, transparent)', marginBottom: 4 }}>
                          {scheduleForm.frequency === 'biweekly' ? 'Pick 2 days' : 'Pick a day'}
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => {
                            const sel = scheduleForm.selectedDays.includes(day);
                            return (
                              <button
                                key={day}
                                onClick={() => setScheduleForm((f) => {
                                  const maxDays = f.frequency === 'biweekly' ? 2 : 1;
                                  const days = sel
                                    ? f.selectedDays.filter((d) => d !== day)
                                    : [...f.selectedDays, day].slice(-maxDays);
                                  return { ...f, selectedDays: days };
                                })}
                                style={{
                                  flex: 1,
                                  padding: '4px 0',
                                  fontSize: '0.6rem',
                                  fontWeight: 600,
                                  borderRadius: 6,
                                  border: sel ? '2px solid var(--primary)' : '1px solid color-mix(in srgb, var(--foreground) 15%, transparent)',
                                  background: sel ? 'color-mix(in srgb, var(--primary) 20%, transparent)' : 'transparent',
                                  color: sel ? 'var(--primary)' : 'var(--foreground)',
                                  cursor: 'pointer',
                                }}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Custom schedule input */}
                    {scheduleForm.frequency === 'custom' && (
                      <input
                        type="text"
                        placeholder='e.g. "every 2 hours", "MWF at 3pm"'
                        value={scheduleForm.customSchedule}
                        onChange={(e) => setScheduleForm((f) => ({ ...f, customSchedule: e.target.value }))}
                        autoFocus
                        style={{
                          width: '100%',
                          fontSize: '0.75rem',
                          padding: '7px 10px',
                          borderRadius: 8,
                          border: '1px solid color-mix(in srgb, var(--foreground) 15%, transparent)',
                          background: 'color-mix(in srgb, var(--foreground) 5%, transparent)',
                          color: 'var(--foreground)',
                          outline: 'none',
                        }}
                      />
                    )}

                    {/* Date & Time row */}
                    <div>
                      <div style={{ fontSize: '0.65rem', color: 'color-mix(in srgb, var(--foreground) 60%, transparent)', marginBottom: 4 }}>
                        {scheduleForm.frequency === 'once' ? 'When' : 'Starting from'}
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input
                          type="date"
                          value={scheduleForm.dueDate}
                          onChange={(e) => setScheduleForm((f) => ({ ...f, dueDate: e.target.value }))}
                          style={{
                            flex: 1,
                            fontSize: '0.7rem',
                            padding: '6px 8px',
                            borderRadius: 8,
                            border: '1px solid color-mix(in srgb, var(--foreground) 15%, transparent)',
                            background: 'color-mix(in srgb, var(--foreground) 5%, transparent)',
                            color: 'var(--foreground)',
                          }}
                        />
                        <input
                          type="time"
                          value={scheduleForm.dueTime}
                          onChange={(e) => setScheduleForm((f) => ({ ...f, dueTime: e.target.value }))}
                          style={{
                            width: 100,
                            fontSize: '0.7rem',
                            padding: '6px 8px',
                            borderRadius: 8,
                            border: '1px solid color-mix(in srgb, var(--foreground) 15%, transparent)',
                            background: 'color-mix(in srgb, var(--foreground) 5%, transparent)',
                            color: 'var(--foreground)',
                          }}
                        />
                      </div>
                    </div>

                    {/* Create button */}
                    <button
                      onClick={() => {
                        const msg = input.trim();
                        if (!msg && !scheduleForm.frequency && !scheduleForm.dueDate) {
                          toast.error('Enter a task description and pick a schedule');
                          return;
                        }
                        // Build schedule description from selections
                        let scheduleDesc = '';
                        const timeStr = scheduleForm.dueTime || '9:00am';
                        switch (scheduleForm.frequency) {
                          case 'daily':
                            scheduleDesc = `daily at ${timeStr}`;
                            break;
                          case 'weekdays':
                            scheduleDesc = `every weekday at ${timeStr}`;
                            break;
                          case 'weekly':
                            scheduleDesc = scheduleForm.selectedDays.length
                              ? `weekly on ${scheduleForm.selectedDays.join(', ')} at ${timeStr}`
                              : `weekly at ${timeStr}`;
                            break;
                          case 'biweekly':
                            scheduleDesc = scheduleForm.selectedDays.length
                              ? `every ${scheduleForm.selectedDays.join(' and ')} at ${timeStr}`
                              : `twice a week at ${timeStr}`;
                            break;
                          case 'monthly':
                            scheduleDesc = `monthly on the ${scheduleForm.dueDate ? new Date(scheduleForm.dueDate + 'T00:00').getDate() + ordSuffix(new Date(scheduleForm.dueDate + 'T00:00').getDate()) : '1st'} at ${timeStr}`;
                            break;
                          case 'custom':
                            scheduleDesc = scheduleForm.customSchedule;
                            break;
                          case 'once':
                          default:
                            break;
                        }
                        const schedulePart = scheduleDesc ? ` [Schedule: ${scheduleDesc}]` : '';
                        const duePart = scheduleForm.dueDate
                          ? ` [Due: ${scheduleForm.dueDate}${scheduleForm.dueTime ? ' ' + scheduleForm.dueTime : ''}]`
                          : '';
                        const fullMsg = msg
                          ? `Create a scheduled task: ${msg}${schedulePart}${duePart}`
                          : `Create a scheduled task${schedulePart}${duePart}`;
                        setInput(fullMsg);
                        setShowSchedulePopup(false);
                        setScheduleForm({ frequency: '', customSchedule: '', dueDate: '', dueTime: '', selectedDays: [] });
                        setTimeout(() => {
                          const textarea = document.querySelector('textarea');
                          if (textarea) {
                            textarea.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
                          }
                        }, 50);
                      }}
                      style={{
                        padding: '8px 14px',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        borderRadius: 8,
                        border: 'none',
                        background: 'var(--primary)',
                        color: 'var(--primary-foreground)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                      }}
                    >
                      <CalendarClock size={13} />
                      {scheduleForm.frequency === 'once' ? 'Schedule Task' : scheduleForm.frequency ? `Schedule ${scheduleForm.frequency === 'custom' ? '' : scheduleForm.frequency.charAt(0).toUpperCase() + scheduleForm.frequency.slice(1) + ' '}Task` : 'Create Scheduled Task'}
                    </button>
                  </div>
                )}
              </div>

              {/* Center: model + multi-agent + persona */}
              <div className={styles.modelSelector}>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className={styles.modelSelect}
                  title="Select AI model"
                >
                  {availableModels.map((m) => (
                    <option key={m.model} value={m.model}>
                      {m.name || m.model}
                    </option>
                  ))}
                </select>
                <ChevronDown size={12} className={styles.modelSelectIcon} />
              </div>

              <div className={styles.agentModeToggle} title="Agent mode">
                {([
                  { mode: 'auto' as const, icon: <Bot size={12} />, label: 'Auto' },
                  { mode: 'cloud_browser' as const, icon: <BrowserIcon size={12} />, label: 'Cloud Browser' },
                  { mode: 'extension' as const, icon: <Puzzle size={12} />, label: 'Extension' },
                  { mode: 'desktop' as const, icon: <Monitor size={12} />, label: 'Desktop' },
                ]).map(({ mode, icon, label }) => (
                  <button
                    key={mode}
                    className={`${styles.agentModeBtn} ${agentMode === mode ? styles.agentModeBtnActive : ''}`}
                    onClick={() => setAgentMode(mode)}
                    title={label}
                  >
                    {icon}
                  </button>
                ))}
              </div>

              <div className={styles.multiAgentToggle} title="Multi-agent mode">
                {(['off', 'parallel', 'sequential'] as const).map((mode) => (
                  <button
                    key={mode}
                    className={`${styles.multiAgentBtn} ${multiAgentMode === mode ? styles.multiAgentBtnActive : ''}`}
                    onClick={() => setMultiAgentMode(mode)}
                    title={
                      mode === 'off'
                        ? 'Single agent'
                        : mode === 'parallel'
                          ? 'Parallel agents (Researcher + Executor + Synthesizer run concurrently)'
                          : 'Sequential agents (each agent builds on the previous)'
                    }
                  >
                    {mode === 'off' ? '1×' : mode === 'parallel' ? '🔀' : '🔁'}
                  </button>
                ))}
              </div>

              {/* Right group: persona + stop/send */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginLeft: 'auto' }}>
                <PersonaSelector sessionId="taskingbot-global" />
                {pendingMessageIds.size > 0 || streamingContent ? (
                  <>
                    <button
                      className={styles.sendButton}
                      onClick={stopGeneration}
                      title="Stop generation"
                      style={{ background: '#ef4444', borderColor: '#ef4444', color: '#fff' }}
                    >
                      <Square size={12} fill="currentColor" />
                    </button>
                    {input.trim() && (
                      <button
                        className={styles.sendButton}
                        onClick={sendMessage}
                        title="Queue message"
                      >
                        <Send size={12} />
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    className={styles.sendButton}
                    onClick={sendMessage}
                    disabled={!input.trim() && attachments.length === 0}
                    title="Send message"
                  >
                    <Send size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
          </>
        )}
      </div>
      </div>

      <SkillSearchModal
        open={isSkillSearchOpen}
        onClose={() => setIsSkillSearchOpen(false)}
        onInstallSkill={(command) => {
          setIsSkillSearchOpen(false);
          void sendMessageText(command);
        }}
        initialQuery={skillSearchQuery}
        searchEndpoint="/_api/taskingbot/skills-search"
      />
      <SkillTreeModal
        open={isSkillTreeOpen}
        onClose={() => setIsSkillTreeOpen(false)}
        sessionId="taskingbot-global"
        initialMode={skillTreeMode}
      />
      <CommandListModal open={isCmdListOpen} onOpenChange={setIsCmdListOpen} />

      {/* Credential prompt modal — auto-triggered when a tool needs credentials */}
      <MissingCredentialModal
        prompt={credentialPrompt.prompt}
        onClose={credentialPrompt.closePrompt}
        onSaved={handleCredentialSaved}
      />
      <DesktopViewerToggle userId={authState.type === 'authenticated' ? authState.user?.id ?? 0 : 0} />
    </>
  );
}
